import { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Plus, DollarSign, Eye, XCircle, CreditCard, Banknote, ArrowRightLeft, Search, Package, Wrench, Trash2, ClipboardList, Dog, User, Clock, Filter, ShoppingBag, Check, ChevronRight, LayoutGrid, AlertTriangle, ArrowRight, Download, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import { Sale, SaleStatus, PaymentMethod, SaleType, Product, ServiceOrder } from '@/types';
import { useSales, useSale, useCreateSale, useCancelSale } from '@/hooks/useSales';
import { useProducts, useCategories } from '@/hooks/useProducts';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import { productsService } from '@/services/products';
import { useOwners } from '@/hooks/useOwners';
import { SaleItemCreate } from '@/services/sales';
import { usePendingServiceOrders, useChargeServiceOrder } from '@/hooks/useServiceOrders';
import { useCashRegisterStatus } from '@/hooks/useCashRegister';
import { formatPrice, formatDateTime } from '@/utils/format';
import { descargarCsv, fechaHoraCsv, numeroCsv } from '@/utils/csv';
import { useAuthStore } from '@/context/authStore';
import { generateSaleReceipt, generateServiceOrderReceipt } from '@/services/pdfService';
import ComprobanteModal from '@/components/sales/ComprobanteModal';

type ViewTab = 'history' | 'new' | 'orders';

const PAYMENT_METHOD_OPTIONS = [
  { value: '', label: 'Todos los métodos' },
  { value: 'cash', label: 'Efectivo' },
  { value: 'card', label: 'Tarjeta' },
  { value: 'transfer', label: 'Transferencia' },
];

const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Efectivo',
  card: 'Tarjeta',
  transfer: 'Transferencia',
  other: 'Otro',
};

const PAYMENT_METHOD_ICONS: Record<PaymentMethod, React.ReactNode> = {
  cash: <Banknote className="h-5 w-5" />,
  card: <CreditCard className="h-5 w-5" />,
  transfer: <ArrowRightLeft className="h-5 w-5" />,
  other: <DollarSign className="h-5 w-5" />,
};

const STATUS_LABELS: Record<SaleStatus, string> = {
  pending: 'Pendiente',
  completed: 'Completada',
  cancelled: 'Cancelada',
};

const STATUS_COLORS: Record<SaleStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const TYPE_LABELS: Record<SaleType, string> = {
  direct: 'Directa',
  consultation: 'Consulta/Comanda',
};

// Tipo para items del carrito (puede ser producto o item personalizado)
interface CartItem {
  id: string; // ID único para el carrito (product.id o generado para custom)
  product?: Product; // undefined para items personalizados
  customName?: string; // Nombre para items personalizados
  quantity: number;
  customPrice?: number; // Precio personalizado
  mlQuantity?: number; // Cantidad en ml para productos sell_by_ml
  // Descuento por línea (opcional): % o monto fijo sobre el total de la línea
  discountType?: 'percentage' | 'fixed';
  discountValue?: number;
}

// Contador para ids únicos de líneas (al separar una unidad en su propia línea)
let cartLineSeq = 0;

// Una venta "en espera": snapshot de la venta en curso para atender otra en paralelo
interface ParkedSale {
  id: number;
  items: CartItem[];
  ownerId: string;
  payment: PaymentMethod;
  notes: string;
  discountType: 'percentage' | 'fixed';
  discountValue: string;
  cardType: 'debit' | 'credit';
  cardInstallments: number;
  chargeAsCash: boolean;
}
let parkedSeq = 0;

export default function Sales() {
  const { user, tenant } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  // Multiplicador de recargo para tarjeta (configurable por tenant)
  const cardSurchargePercent = tenant?.card_surcharge_percent ?? 10;
  const cardSurchargeMultiplier = 1 + (cardSurchargePercent / 100);

  // --- Helpers de cálculo por línea (precio base, descuento y total) ---
  // Nivel de precio según el medio de pago:
  //   'cash'        -> precio efectivo (sale_price)
  //   'electronic'  -> precio de lista / tarjeta (list_price): débito, transferencia, crédito 1 cuota
  //   'installment' -> precio crédito en cuotas (installment_price, cae a list_price si no está cargado)
  type PriceTier = 'cash' | 'electronic' | 'installment';

  // Precio unitario base según el nivel de precio
  const unitBaseFor = (item: CartItem, tier: PriceTier): number => {
    const isCard = tier !== 'cash';
    if (!item.product) {
      const p = item.customPrice ?? 0;
      return isCard ? Math.round(p * cardSurchargeMultiplier) : p;
    }
    if (item.product.sell_by_ml) {
      const p = (item.product.price_per_ml ?? 0) * (item.mlQuantity ?? 0);
      return isCard ? Math.round(p * cardSurchargeMultiplier) : p;
    }
    if (item.customPrice != null) {
      return isCard ? Math.round(item.customPrice * cardSurchargeMultiplier) : item.customPrice;
    }
    if (tier === 'installment') {
      return item.product.installment_price ?? item.product.list_price ?? item.product.sale_price ?? 0;
    }
    if (tier === 'electronic') {
      return item.product.list_price ?? item.product.sale_price ?? 0;
    }
    return item.product.sale_price ?? 0;
  };

  // Monto de descuento de la línea (% o fijo sobre el total de la línea)
  const lineDiscountFor = (item: CartItem, tier: PriceTier): number => {
    const base = unitBaseFor(item, tier) * item.quantity;
    const v = item.discountValue ?? 0;
    if (!v || v <= 0) return 0;
    if (item.discountType === 'fixed') return Math.min(base, v);
    return base * (Math.min(v, 100) / 100);
  };

  // Total de la línea ya con su descuento aplicado
  const lineTotalFor = (item: CartItem, tier: PriceTier): number =>
    Math.max(0, unitBaseFor(item, tier) * item.quantity - lineDiscountFor(item, tier));

  // Precio unitario final (descuento horneado) para mandar al backend
  const finalUnitPriceFor = (item: CartItem, tier: PriceTier): number => {
    const qty = item.quantity || 1;
    return Math.round((lineTotalFor(item, tier) / qty) * 100) / 100;
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<ViewTab>('history');

  // Leer tab del query param al cargar
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'new' || tabParam === 'orders') {
      setActiveTab(tabParam);
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  // Filtros historial
  const [paymentFilter, setPaymentFilter] = useState<PaymentMethod | ''>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [cancelConfirm, setCancelConfirm] = useState<Sale | null>(null);

  // Nueva venta
  const [newSaleItems, setNewSaleItems] = useState<CartItem[]>([]);
  const [newSalePayment, setNewSalePayment] = useState<PaymentMethod>('cash');
  // Pago dividido: el cajero carga cuanto entro por cada medio. Los montos tienen que
  // sumar el total, porque de ahi sale el arqueo de caja.
  const [splitMode, setSplitMode] = useState(false);
  const [splitCash, setSplitCash] = useState('');
  const [splitCard, setSplitCard] = useState('');
  const [splitTransfer, setSplitTransfer] = useState('');
  const [newSaleOwnerId, setNewSaleOwnerId] = useState<string>('');
  const [ownerSearch, setOwnerSearch] = useState('');
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
  const ownerDropdownRef = useRef<HTMLDivElement>(null);

  const [newSaleNotes, setNewSaleNotes] = useState('');
  const [showConfirmSale, setShowConfirmSale] = useState(false);
  // Ventas en espera (para atender varias en paralelo)
  const [parkedSales, setParkedSales] = useState<ParkedSale[]>([]);
  // Id de la venta que esta abierta en el formulario. Las pestañas se ordenan por
  // saleOrder y NO se reordenan al cambiar: si no, no se sabe en cual estas parada.
  const [currentSaleId, setCurrentSaleId] = useState<number>(() => ++parkedSeq);
  const [saleOrder, setSaleOrder] = useState<number[]>([]);
  // Tracking interno de tarjeta: débito/crédito + cuotas (no cambia el monto)
  const [cardType, setCardType] = useState<'debit' | 'credit'>('debit');
  const [cardInstallments, setCardInstallments] = useState(3);
  // Cobrar esta venta al precio de efectivo aunque el medio de pago sea otro.
  const [chargeAsCash, setChargeAsCash] = useState(false);
  // Al restaurar una venta en espera hay que respetar el check que tenia guardado:
  // sin esto el efecto del valor por defecto lo pisaba apenas volvia a la pantalla.
  const skipChargeAsCashDefault = useRef(false);
  const [productSearch, setProductSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  // Item personalizado (Otro)
  const [showCustomItemForm, setShowCustomItemForm] = useState(false);
  const [customItemName, setCustomItemName] = useState('');
  const [customItemPrice, setCustomItemPrice] = useState('');

  // Estado para comandas
  const [selectedOrder, setSelectedOrder] = useState<ServiceOrder | null>(null);
  const [orderPaymentMethod, setOrderPaymentMethod] = useState<PaymentMethod>('cash');
  // Cobrar la comanda al precio de efectivo aunque se pague con tarjeta.
  const [orderChargeAsCash, setOrderChargeAsCash] = useState(false);
  const [orderProductSearch, setOrderProductSearch] = useState('');
  const [orderCategoryFilter, setOrderCategoryFilter] = useState<string>('all');
  const [isChargeModalOpen, setIsChargeModalOpen] = useState(false);
  const [orderExtraItems, setOrderExtraItems] = useState<Array<{ product?: Product; quantity: number; customName?: string; customPrice?: number }>>([]);
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [discountValue, setDiscountValue] = useState('');
  const [directDiscountType, setDirectDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [directDiscountValue, setDirectDiscountValue] = useState('');
  // Redondeo en efectivo (botón manual, opt-in por vete)
  const [applyRounding, setApplyRounding] = useState(false);

  // Item personalizado para comandas
  const [showOrderCustomItemForm, setShowOrderCustomItemForm] = useState(false);
  const [orderCustomItemName, setOrderCustomItemName] = useState('');
  const [orderCustomItemPrice, setOrderCustomItemPrice] = useState('');

  // Cerrar dropdown de clientes al click afuera
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ownerDropdownRef.current && !ownerDropdownRef.current.contains(e.target as Node)) {
        setShowOwnerDropdown(false);
      }
    };
    if (showOwnerDropdown) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOwnerDropdown]);

  // Queries
  const { data: sales = [], isLoading } = useSales({
    payment_method: paymentFilter || undefined,
    date_from: dateFrom ? `${dateFrom}T00:00:00` : undefined,
    date_to: dateTo ? `${dateTo}T23:59:59` : undefined,
  });
  const { data: saleDetail } = useSale(selectedSale?.id ?? null);
  const { data: categories = [] } = useCategories();

  // SIEMPRE cargar productos - más simple para el usuario
  const { data: allProducts = [], isLoading: isLoadingProducts } = useProducts(
    { only_active: true },
    { enabled: activeTab === 'new' || activeTab === 'orders' }
  );

  const { data: owners = [] } = useOwners();
  const { data: pendingOrders = [], isLoading: isLoadingOrders } = usePendingServiceOrders();
  const { data: cashStatus } = useCashRegisterStatus();

  // Verificar si tiene permiso de caja
  const hasCashPermission = user?.effective_permissions?.includes('cash_register');
  const isCashClosed = hasCashPermission && cashStatus && !cashStatus.is_open;

  // Mutations
  const createSale = useCreateSale();
  // Venta para la que se esta eligiendo comprobante (ticket comun o factura fiscal).
  const [comprobanteSale, setComprobanteSale] = useState<Sale | null>(null);
  const cancelSale = useCancelSale();
  const chargeOrder = useChargeServiceOrder();

  // Filtrar productos según búsqueda y categoría
  const filteredProducts = useMemo(() => {
    // Los productos a granel no se venden: son el "tanque" de stock del que salen los
    // fraccionados. Si se vendieran directo, cada unidad descontaria 1 kg en vez de la
    // bolsa entera, porque su stock esta en kg. Se vende la fraccion.
    let result = allProducts.filter((p) => !p.is_bulk);

    // Filtrar por búsqueda
    if (productSearch.length >= 2) {
      const search = productSearch.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(search) ||
        (p.code && p.code.toLowerCase().includes(search))
      );
    }

    // Filtrar por categoría
    if (categoryFilter !== 'all') {
      result = result.filter(p => p.category_id === parseInt(categoryFilter));
    }

    // Ordenar alfabéticamente por nombre
    return [...result].sort((a, b) =>
      a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })
    );
  }, [allProducts, productSearch, categoryFilter]);

  // Si lo que buscan es un producto a granel, se avisa por que no aparece en la lista
  const hiddenBulkMatches = useMemo(() => {
    if (productSearch.length < 2) return [];
    const search = productSearch.toLowerCase();
    return allProducts.filter(
      (p) =>
        p.is_bulk &&
        (p.name.toLowerCase().includes(search) ||
          (p.code && p.code.toLowerCase().includes(search)))
    );
  }, [allProducts, productSearch]);

  // Filtrar productos para comandas
  const filteredOrderProducts = useMemo(() => {
    // Idem ventas: el granel no se carga, se carga la fraccion
    let result = allProducts.filter((p) => !p.is_bulk);

    if (orderProductSearch.length >= 2) {
      const search = orderProductSearch.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(search) ||
        (p.code && p.code.toLowerCase().includes(search))
      );
    }

    if (orderCategoryFilter !== 'all') {
      result = result.filter(p => p.category_id === parseInt(orderCategoryFilter));
    }

    // Ordenar alfabéticamente por nombre
    return [...result].sort((a, b) =>
      a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })
    );
  }, [allProducts, orderProductSearch, orderCategoryFilter]);

  // Nivel de precio del cobro actual según el medio de pago elegido.
  // Si la vete NO tiene precios por medio de pago, se mantiene el comportamiento
  // clásico: tarjeta => precio de lista (electronic), resto => efectivo (cash).
  const priceByPayment = tenant?.price_by_payment_method ?? false;

  // Algunas veterinarias no trasladan el costo de la tarjeta cuando no hay financiación:
  // débito o crédito en 1 cuota se cobran igual que en efectivo. Antes había que cargar
  // el precio recargado y descontar a mano, que no vuelve al original: el descuento se
  // aplica sobre el precio ya recargado ($2000 +10% −10% = $1980).
  const unaCuotaComoEfectivo = tenant?.card_single_installment_as_cash ?? false;
  const esTarjetaSinCuotas =
    newSalePayment === 'card' && !(cardType === 'credit' && cardInstallments >= 2);

  const currentTier: PriceTier = useMemo(() => {
    // El check manda: la veterinaria decide venta por venta. El flag del tenant solo
    // define si viene tildado por defecto (ver el efecto de abajo).
    if (chargeAsCash) return 'cash';

    if (!priceByPayment) {
      return newSalePayment === 'card' ? 'electronic' : 'cash';
    }
    // 3 niveles: efectivo / electrónicos / crédito en cuotas
    if (newSalePayment === 'transfer') return 'electronic';
    if (newSalePayment === 'card') {
      if (cardType === 'credit' && cardInstallments >= 2) return 'installment';
      return 'electronic'; // débito o crédito en 1 cuota
    }
    return 'cash'; // efectivo u otro
  }, [priceByPayment, newSalePayment, cardType, cardInstallments, chargeAsCash]);

  // Al cambiar el medio de pago el check vuelve a su valor por defecto: tildado solo si
  // la veterinaria cobra la tarjeta sin financiacion como efectivo. Si el cajero lo
  // toca despues, su eleccion manda hasta que vuelva a cambiar el medio de pago.
  // La comanda no distingue cuotas, asi que con tarjeta vale el mismo criterio que
  // en la venta directa sin financiacion.
  useEffect(() => {
    setOrderChargeAsCash(unaCuotaComoEfectivo && orderPaymentMethod === 'card');
  }, [orderPaymentMethod, unaCuotaComoEfectivo, selectedOrder?.id]);

  useEffect(() => {
    if (skipChargeAsCashDefault.current) {
      skipChargeAsCashDefault.current = false;
      return;
    }
    setChargeAsCash(unaCuotaComoEfectivo && esTarjetaSinCuotas);
  }, [newSalePayment, cardType, cardInstallments, unaCuotaComoEfectivo, esTarjetaSinCuotas]);

  // Total del cobro con el nivel de precio actual
  const newSaleTotal = useMemo(() => {
    let total = 0;
    newSaleItems.forEach((item) => {
      total += lineTotalFor(item, currentTier);
    });
    return total;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newSaleItems, currentTier, cardSurchargeMultiplier]);

  const directDiscountNum = parseFloat(directDiscountValue) || 0;
  const newSaleTotalWithDiscount = (() => {
    if (directDiscountNum <= 0) return newSaleTotal;
    if (directDiscountType === 'percentage') return Math.max(0, newSaleTotal * (1 - directDiscountNum / 100));
    return Math.max(0, newSaleTotal - directDiscountNum);
  })();

  // Redondeo en efectivo: solo disponible si la vete lo tiene activado y el pago es efectivo.
  const cashRoundingEnabled = tenant?.cash_rounding_enabled ?? false;
  const canRound = cashRoundingEnabled && newSalePayment === 'cash';
  const roundedTotal = Math.round(newSaleTotalWithDiscount / 100) * 100;  // al 100 más cercano
  const roundingActive = canRound && applyRounding;
  const roundingAdjustment = roundingActive ? roundedTotal - newSaleTotalWithDiscount : 0;
  // Total que realmente se cobra (con redondeo si está aplicado)
  const finalChargeTotal = roundingActive ? roundedTotal : newSaleTotalWithDiscount;

  const splitNum = (v: string) => parseFloat(v.replace(',', '.')) || 0;
  const splitCashNum = splitNum(splitCash);
  const splitCardNum = splitNum(splitCard);
  const splitTransferNum = splitNum(splitTransfer);
  const splitSum = splitCashNum + splitCardNum + splitTransferNum;
  const splitFalta = finalChargeTotal - splitSum;
  // Se compara con tolerancia de un centavo: los decimales del descuento no tienen
  // por que dar exacto y no vale trabar el cobro por eso.
  const splitCierra = Math.abs(splitFalta) < 0.01;
  const splitMediosUsados = [splitCashNum, splitCardNum, splitTransferNum].filter((m) => m > 0).length;
  const splitValido = !splitMode || (splitCierra && splitMediosUsados >= 2);

  const hasItemsWithoutPrice = useMemo(() => {
    return newSaleItems.some(item => {
      // Item personalizado sin precio
      if (!item.product) {
        return item.customPrice == null || item.customPrice <= 0;
      }
      // Producto ml sin cantidad
      if (item.product.sell_by_ml) {
        return !item.mlQuantity || item.mlQuantity <= 0;
      }
      // Servicio sin precio
      return item.product.is_service &&
        item.product.sale_price == null &&
        item.customPrice == null;
    });
  }, [newSaleItems]);

  const handleViewDetail = (sale: Sale) => {
    setSelectedSale(sale);
    setIsDetailModalOpen(true);
  };

  const handleCancel = async () => {
    if (cancelConfirm) {
      try {
        await cancelSale.mutateAsync(cancelConfirm.id);
        setCancelConfirm(null);
      } catch (error) {
        console.error('Error cancelling:', error);
      }
    }
  };

  const handleAddProduct = (product: Product) => {
    const existing = newSaleItems.find((item) => item.product?.id === product.id);
    if (existing) {
      setNewSaleItems(
        newSaleItems.map((item) =>
          item.product?.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        )
      );
    } else {
      setNewSaleItems([...newSaleItems, {
        id: `product-${product.id}`,
        product,
        quantity: 1,
        customPrice: undefined,
        mlQuantity: product.sell_by_ml ? undefined : undefined
      }]);
    }
  };

  const handleAddCustomItem = () => {
    if (!customItemName.trim() || !customItemPrice) return;
    const newItem: CartItem = {
      id: `custom-${Date.now()}`,
      customName: customItemName.trim(),
      quantity: 1,
      customPrice: parseFloat(customItemPrice)
    };
    setNewSaleItems([...newSaleItems, newItem]);
    setCustomItemName('');
    setCustomItemPrice('');
    setShowCustomItemForm(false);
  };

  const handleUpdatePrice = (itemId: string, price: number | undefined) => {
    setNewSaleItems(
      newSaleItems.map((item) =>
        item.id === itemId ? { ...item, customPrice: price } : item
      )
    );
  };

  const handleUpdateMlQuantity = (itemId: string, ml: number | undefined) => {
    setNewSaleItems(
      newSaleItems.map((item) =>
        item.id === itemId ? { ...item, mlQuantity: ml } : item
      )
    );
  };

  const handleRemoveItem = (itemId: string) => {
    setNewSaleItems(newSaleItems.filter((item) => item.id !== itemId));
  };

  const handleUpdateQuantity = (itemId: string, quantity: number) => {
    if (quantity < 1) return;
    setNewSaleItems(
      newSaleItems.map((item) =>
        item.id === itemId ? { ...item, quantity } : item
      )
    );
  };

  // Setear/actualizar el descuento de una línea (tipo y/o valor)
  const handleSetLineDiscount = (
    itemId: string,
    patch: Partial<Pick<CartItem, 'discountType' | 'discountValue'>>
  ) => {
    setNewSaleItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, ...patch } : item))
    );
  };

  // Separar una unidad de la línea en una línea nueva (para descuento distinto)
  const handleSplitLine = (itemId: string) => {
    setNewSaleItems((prev) => {
      const idx = prev.findIndex((i) => i.id === itemId);
      if (idx === -1 || prev[idx].quantity <= 1) return prev;
      const item = prev[idx];
      const newLine: CartItem = {
        ...item,
        id: `${item.id}-split-${++cartLineSeq}`,
        quantity: 1,
        // La nueva línea arranca sin descuento (para ponerle uno propio)
        discountType: undefined,
        discountValue: undefined,
      };
      const copy = [...prev];
      copy[idx] = { ...item, quantity: item.quantity - 1 };
      copy.splice(idx + 1, 0, newLine);
      return copy;
    });
  };

  // --- Ventas en espera (varias en paralelo) ---
  const clearCurrentSale = () => {
    setNewSaleItems([]);
    setNewSalePayment('cash');
    setSplitMode(false);
    setSplitCash(''); setSplitCard(''); setSplitTransfer('');
    setNewSaleOwnerId('');
    setNewSaleNotes('');
    setDirectDiscountValue('');
    setDirectDiscountType('percentage');
    setCardType('debit');
    setCardInstallments(3);
    setChargeAsCash(false);
    setApplyRounding(false);
  };

  const snapshotCurrentSale = (): ParkedSale => ({
    id: currentSaleId,
    items: newSaleItems,
    ownerId: newSaleOwnerId,
    payment: newSalePayment,
    notes: newSaleNotes,
    discountType: directDiscountType,
    discountValue: directDiscountValue,
    cardType,
    cardInstallments,
    chargeAsCash,
  });

  const restoreSale = (s: ParkedSale) => {
    setNewSaleItems(s.items);
    setNewSaleOwnerId(s.ownerId);
    setNewSalePayment(s.payment);
    setNewSaleNotes(s.notes);
    setDirectDiscountType(s.discountType);
    setDirectDiscountValue(s.discountValue);
    setCardType(s.cardType);
    setCardInstallments(s.cardInstallments);
    skipChargeAsCashDefault.current = true;
    setChargeAsCash(s.chargeAsCash);
  };

  // Guardar la venta actual en espera y abrir una nueva vacía
  const handleNewParallelSale = () => {
    const nuevoId = ++parkedSeq;
    if (newSaleItems.length > 0) {
      setParkedSales([...parkedSales, snapshotCurrentSale()]);
      setSaleOrder((prev) => [...ordenConActual(prev), nuevoId]);
    } else {
      // La actual esta vacía: se reemplaza por la nueva y no deja pestaña colgada.
      setSaleOrder((prev) => [...prev.filter((x) => x !== currentSaleId), nuevoId]);
    }
    setCurrentSaleId(nuevoId);
    clearCurrentSale();
  };

  // Cambiar a otra venta abierta. La actual se guarda en espera SIN moverse de lugar.
  const handleSwitchToParked = (id: number) => {
    if (id === currentSaleId) return;
    const target = parkedSales.find((p) => p.id === id);
    if (!target) return;

    const rest = parkedSales.filter((p) => p.id !== id);
    if (newSaleItems.length > 0) {
      setParkedSales([...rest, snapshotCurrentSale()]);
      setSaleOrder((prev) => ordenConActual(prev));
    } else {
      // Venta vacía: se descarta su pestaña al salir de ella.
      setParkedSales(rest);
      setSaleOrder((prev) => prev.filter((x) => x !== currentSaleId));
    }
    setCurrentSaleId(id);
    restoreSale(target);
  };

  // Descartar una venta abierta (la actual o una en espera)
  const handleCloseParked = (id: number) => {
    if (id === currentSaleId) {
      if (newSaleItems.length > 0 && !window.confirm('¿Descartar esta venta?')) return;
      const siguiente = parkedSales[parkedSales.length - 1];
      setSaleOrder((prev) => prev.filter((x) => x !== id));
      if (siguiente) {
        setParkedSales((prev) => prev.filter((p) => p.id !== siguiente.id));
        setCurrentSaleId(siguiente.id);
        restoreSale(siguiente);
      } else {
        clearCurrentSale();
      }
      return;
    }
    const target = parkedSales.find((p) => p.id === id);
    if (target && target.items.length > 0 && !window.confirm('¿Descartar esta venta en espera?')) return;
    setParkedSales((prev) => prev.filter((p) => p.id !== id));
    setSaleOrder((prev) => prev.filter((x) => x !== id));
  };

  // La venta actual puede no estar todavía en el orden (es la primera del día).
  const ordenConActual = (prev: number[]) =>
    prev.includes(currentSaleId) ? prev : [...prev, currentSaleId];

  const handleCreateSale = async () => {
    if (newSaleItems.length === 0) return;
    if (hasItemsWithoutPrice) return;

    const items: SaleItemCreate[] = newSaleItems.map((item) => {
      // Precio unitario final = precio del nivel elegido con el descuento de
      // línea ya horneado (unit_price * quantity = total de la línea).
      const unitPrice = finalUnitPriceFor(item, currentTier);
      if (!item.product) {
        return {
          custom_name: item.customName,
          quantity: item.quantity,
          unit_price: unitPrice,
        };
      }
      if (item.product.sell_by_ml && item.mlQuantity) {
        return {
          product_id: item.product.id,
          quantity: item.quantity,
          unit_price: unitPrice,
          ml_quantity: item.mlQuantity,
        };
      }
      return {
        product_id: item.product.id,
        quantity: item.quantity,
        unit_price: unitPrice,
      };
    });

    try {
      const createdSale = await createSale.mutateAsync({
        sale_type: 'direct',
        owner_id: newSaleOwnerId ? parseInt(newSaleOwnerId) : undefined,
        items,
        payment_method: newSalePayment,
        notes: newSaleNotes || undefined,
        discount_percentage: directDiscountType === 'percentage' && directDiscountNum > 0 ? directDiscountNum : undefined,
        discount_amount: directDiscountType === 'fixed' && directDiscountNum > 0 ? directDiscountNum : undefined,
        rounding_adjustment: roundingAdjustment !== 0 ? roundingAdjustment : undefined,
        is_split: splitMode || undefined,
        amount_cash: splitMode ? splitCashNum : undefined,
        amount_card: splitMode ? splitCardNum : undefined,
        amount_transfer: splitMode ? splitTransferNum : undefined,
        card_type: newSalePayment === 'card' ? cardType : undefined,
        card_installments: newSalePayment === 'card' && cardType === 'credit' ? cardInstallments : undefined,
      });

      setShowConfirmSale(false);
      setComprobanteSale(createdSale);
      // La venta cobrada deja de existir: se saca su pestaña y se pasa a la siguiente.
      setSaleOrder((prev) => prev.filter((x) => x !== currentSaleId));
      if (parkedSales.length > 0) {
        const next = parkedSales[parkedSales.length - 1];
        setParkedSales((prev) => prev.filter((p) => p.id !== next.id));
        setCurrentSaleId(next.id);
        restoreSale(next);
      } else {
        setCurrentSaleId(++parkedSeq);
        clearCurrentSale();
        setActiveTab('history');
      }
    } catch (error) {
      console.error('Error creating sale:', error);
    }
  };

  // Handlers para comandas
  const handleSelectOrder = (order: ServiceOrder) => {
    setSelectedOrder(order);
    setOrderPaymentMethod('cash');
    setOrderProductSearch('');
    setOrderCategoryFilter('all');
    setOrderExtraItems([]);
    setShowOrderCustomItemForm(false);
    setOrderCustomItemName('');
    setOrderCustomItemPrice('');
  };

  const handleAddExtraProduct = (product: Product) => {
    const existing = orderExtraItems.find((item) => item.product?.id === product.id);
    if (existing) {
      setOrderExtraItems(
        orderExtraItems.map((item) =>
          item.product?.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        )
      );
    } else {
      setOrderExtraItems([...orderExtraItems, { product, quantity: 1 }]);
    }
  };

  const handleUpdateExtraQuantity = (productId: number, quantity: number) => {
    if (quantity < 1) {
      setOrderExtraItems(orderExtraItems.filter((item) => item.product?.id !== productId));
    } else {
      setOrderExtraItems(
        orderExtraItems.map((item) =>
          item.product?.id === productId ? { ...item, quantity } : item
        )
      );
    }
  };

  const handleRemoveExtraItem = (productId: number) => {
    setOrderExtraItems(orderExtraItems.filter((item) => item.product?.id !== productId));
  };

  // --- Lector de código de barras ---
  // Al escanear, busca el producto por barcode y lo agrega al carrito
  // (Nueva Venta) o a la comanda (Comandas), según la pestaña activa.
  const handleBarcodeScan = async (code: string) => {
    let product: Product | null = allProducts.find((p) => p.barcode === code) || null;
    if (!product) {
      try {
        product = await productsService.getProductByBarcode(code);
      } catch {
        toast.error('Error al buscar el código de barras');
        return;
      }
    }
    if (!product) {
      toast.error(`No hay un producto con el código ${code}`);
      return;
    }
    if (activeTab === 'orders') {
      handleAddExtraProduct(product);
      setOrderProductSearch('');
    } else {
      handleAddProduct(product);
      setProductSearch('');
    }
    toast.success(`${product.name} agregado`);
  };

  // Solo activo en las pantallas de carga (Nueva Venta / Comandas) y sin modales abiertos.
  const scannerEnabled =
    (activeTab === 'new' || activeTab === 'orders') &&
    !showCustomItemForm &&
    !showOrderCustomItemForm &&
    !isDetailModalOpen &&
    !isChargeModalOpen &&
    !cancelConfirm;

  useBarcodeScanner(handleBarcodeScan, { enabled: scannerEnabled });

  // Handler para agregar item customizado en comandas
  const handleAddOrderCustomItem = () => {
    if (!orderCustomItemName.trim() || !orderCustomItemPrice) return;
    const newItem = {
      customName: orderCustomItemName.trim(),
      quantity: 1,
      customPrice: parseFloat(orderCustomItemPrice)
    };
    setOrderExtraItems([...orderExtraItems, newItem]);
    setOrderCustomItemName('');
    setOrderCustomItemPrice('');
    setShowOrderCustomItemForm(false);
  };

  // Handler para actualizar cantidad de item customizado
  const handleUpdateCustomExtraQuantity = (index: number, quantity: number) => {
    if (quantity < 1) {
      setOrderExtraItems(orderExtraItems.filter((_, i) => i !== index));
    } else {
      setOrderExtraItems(
        orderExtraItems.map((item, i) =>
          i === index ? { ...item, quantity } : item
        )
      );
    }
  };

  // Handler para eliminar item customizado
  const handleRemoveCustomExtraItem = (index: number) => {
    setOrderExtraItems(orderExtraItems.filter((_, i) => i !== index));
  };

  // Handler para actualizar precio de extra (servicios)
  const handleUpdateExtraPrice = (productId: number, price: number | undefined) => {
    setOrderExtraItems(
      orderExtraItems.map((item) =>
        item.product?.id === productId ? { ...item, customPrice: price } : item
      )
    );
  };

  // Toda la logica de precio de la comanda cuelga de aca: tarjeta con recargo, salvo
  // que se haya pedido cobrarla al precio de efectivo.
  const orderConRecargo = orderPaymentMethod === 'card' && !orderChargeAsCash;

  const extraItemsTotal = orderExtraItems.reduce((sum, item) => {
    // Item customizado
    if (!item.product) {
      const price = item.customPrice ?? 0;
      // Aplicar recargo configurable para tarjeta
      const finalPrice = (orderConRecargo)
        ? Math.round(price * cardSurchargeMultiplier)
        : price;
      return sum + (finalPrice * item.quantity);
    }
    // Producto normal (usar customPrice si existe, ej: servicios con precio editado)
    let basePrice: number;
    if (item.customPrice != null) {
      basePrice = (orderConRecargo)
        ? Math.round(item.customPrice * cardSurchargeMultiplier)
        : item.customPrice;
    } else {
      basePrice = (orderConRecargo)
        ? (item.product.list_price ?? item.product.sale_price ?? 0)
        : (item.product.sale_price ?? 0);
    }
    return sum + (basePrice * item.quantity);
  }, 0);

  const orderTotal = (() => {
    if (!selectedOrder) return 0;
    if (orderConRecargo) {
      const serviciosCat = categories.find(
        (c) => c.name.toLowerCase() === 'servicios' && c.is_active
      );
      return selectedOrder.items.reduce((sum, item) => {
        const product = item.product_id ? allProducts.find(p => p.id === item.product_id) : null;

        // Item custom (servicio, peluquería): usar multiplicador de categoría "Servicios"
        if (!item.product_id) {
          const multiplier = serviciosCat?.list_price_multiplier || cardSurchargeMultiplier;
          return sum + Math.round(item.unit_price * item.quantity * multiplier);
        }

        // Si es producto por ML, aplicar recargo configurable
        if ((item.ml_quantity && item.ml_quantity > 0) || product?.sell_by_ml) {
          const cat = product?.category_id ? categories.find(c => c.id === product.category_id) : null;
          const multiplier = cat?.list_price_multiplier || cardSurchargeMultiplier;
          return sum + Math.round(item.unit_price * item.quantity * multiplier);
        }

        // Producto normal: usar list_price si existe, o calcular con multiplicador de categoría
        if (product?.list_price) {
          return sum + (product.list_price * item.quantity);
        }
        const cat = product?.category_id ? categories.find(c => c.id === product.category_id) : null;
        const multiplier = cat?.list_price_multiplier || cardSurchargeMultiplier;
        return sum + Math.round(item.unit_price * item.quantity * multiplier);
      }, 0);
    }
    return selectedOrder.total;
  })();

  const orderTotalWithExtras = Number(orderTotal) + Number(extraItemsTotal);

  const discountNum = parseFloat(discountValue) || 0;
  const discountedTotal = (() => {
    if (discountNum <= 0) return orderTotalWithExtras;
    if (discountType === 'percentage') return Math.max(0, orderTotalWithExtras * (1 - discountNum / 100));
    return Math.max(0, orderTotalWithExtras - discountNum);
  })();

  const handleChargeOrder = async () => {
    if (!selectedOrder) return;

    try {
      await chargeOrder.mutateAsync({
        orderId: selectedOrder.id,
        paymentMethod: orderPaymentMethod,
        discountPercentage: discountType === 'percentage' && discountNum > 0 ? discountNum : undefined,
        discountAmount: discountType === 'fixed' && discountNum > 0 ? discountNum : undefined,
        chargeAsCash: orderChargeAsCash,
      });

      if (orderExtraItems.length > 0) {
        const extraItems: SaleItemCreate[] = orderExtraItems.map((item) => {
          // Item customizado (sin producto)
          if (!item.product) {
            let customUnitPrice = item.customPrice ?? 0;
            // Aplicar recargo configurable para tarjeta
            if (orderConRecargo) {
              customUnitPrice = Math.round(customUnitPrice * cardSurchargeMultiplier);
            }
            return {
              custom_name: item.customName,
              quantity: item.quantity,
              unit_price: customUnitPrice,
            };
          }
          // Producto normal (usar customPrice si fue editado, ej: servicios)
          let price: number;
          if (item.customPrice != null) {
            price = (orderConRecargo)
              ? Math.round(item.customPrice * cardSurchargeMultiplier)
              : item.customPrice;
          } else {
            price = (orderConRecargo)
              ? (item.product.list_price ?? item.product.sale_price ?? 0)
              : (item.product.sale_price ?? 0);
          }
          return {
            product_id: item.product.id,
            quantity: item.quantity,
            unit_price: price,
          };
        });

        await createSale.mutateAsync({
          sale_type: 'direct',
          owner_id: selectedOrder.owner_id ?? undefined,
          items: extraItems,
          payment_method: orderPaymentMethod,
          notes: `Productos adicionales - Comanda #${selectedOrder.id}`,
        });
      }

      setSelectedOrder(null);
      setOrderExtraItems([]);
      setIsChargeModalOpen(false);
      setDiscountValue('');
      setDiscountType('percentage');
      setActiveTab('history');
    } catch (error: any) {
      console.error('Error charging order:', error);
      const errorMessage = error.response?.data?.detail || 'Error al cobrar la comanda';
      toast.error(errorMessage);
    }
  };

  const formatCurrency = formatPrice;
  const formatDate = formatDateTime;

  // Controles de descuento por línea + botón separar (para el carrito de venta)
  const renderLineDiscount = (item: CartItem) => {
    const dtype = item.discountType ?? 'percentage';
    const disc = lineDiscountFor(item, currentTier);
    return (
      <div className="mt-3 pt-3 border-t border-cream-100 flex items-center gap-2 flex-wrap">
        <span className="text-xs text-taupe-500">Desc.:</span>
        <div className="flex rounded-lg overflow-hidden border border-cream-300">
          <button
            type="button"
            onClick={() => handleSetLineDiscount(item.id, { discountType: 'percentage' })}
            className={`px-2 py-1 text-xs font-medium ${dtype === 'percentage' ? 'bg-green-100 text-green-700' : 'bg-white text-taupe-500'}`}
          >
            %
          </button>
          <button
            type="button"
            onClick={() => handleSetLineDiscount(item.id, { discountType: 'fixed' })}
            className={`px-2 py-1 text-xs font-medium border-l border-cream-300 ${dtype === 'fixed' ? 'bg-green-100 text-green-700' : 'bg-white text-taupe-500'}`}
          >
            $
          </button>
        </div>
        <input
          type="number"
          min="0"
          placeholder={dtype === 'percentage' ? '%' : '$'}
          value={item.discountValue ?? ''}
          onChange={(e) => handleSetLineDiscount(item.id, { discountValue: e.target.value ? parseFloat(e.target.value) : undefined })}
          className="w-20 px-2 py-1 border border-cream-300 rounded-lg text-sm"
        />
        {disc > 0 && <span className="text-xs text-green-600 font-medium">−{formatCurrency(disc)}</span>}
        {item.quantity > 1 && (
          <button
            type="button"
            onClick={() => handleSplitLine(item.id)}
            className="ml-auto text-xs px-2 py-1 rounded-lg border border-cream-300 text-taupe-600 hover:bg-cream-50"
            title="Separar una unidad en otra línea (para ponerle otro descuento)"
          >
            Separar 1
          </button>
        )}
      </div>
    );
  };

  const totals = useMemo(() => {
    const result = { cash: 0, card: 0, transfer: 0, other: 0, total: 0 };
    sales.forEach((sale) => {
      if (sale.status !== 'cancelled') {
        const saleTotal = Number(sale.total) || 0;
        // Pago dividido: cada medio suma lo suyo. Antes el total entero caia en
        // sale.payment_method y las tarjetas de arriba quedaban infladas.
        if (sale.is_split) {
          result.cash += Number(sale.amount_cash) || 0;
          result.card += Number(sale.amount_card) || 0;
          result.transfer += Number(sale.amount_transfer) || 0;
        } else {
          result[sale.payment_method] += saleTotal;
        }
        result.total += saleTotal;
      }
    });
    return result;
  }, [sales]);

  /**
   * Baja el historial como CSV, con los filtros que estan puestos en pantalla.
   *
   * Va una fila por venta y las columnas de cada medio aparte, para que una venta
   * con pago dividido se pueda leer sin abrirla.
   */
  const descargarHistorial = () => {
    const filas = sales.map((sale) => {
      const montos = sale.is_split
        ? {
            cash: Number(sale.amount_cash) || 0,
            card: Number(sale.amount_card) || 0,
            transfer: Number(sale.amount_transfer) || 0,
          }
        : {
            cash: sale.payment_method === 'cash' ? Number(sale.total) || 0 : 0,
            card: sale.payment_method === 'card' ? Number(sale.total) || 0 : 0,
            transfer: sale.payment_method === 'transfer' ? Number(sale.total) || 0 : 0,
          };
      return [
        sale.receipt_number || '',
        fechaHoraCsv(sale.created_at),
        sale.owner_name || 'Cliente ocasional',
        sale.user_name || '',
        sale.items?.length ?? 0,
        sale.is_split ? 'Dividido' : PAYMENT_METHOD_LABELS[sale.payment_method] || sale.payment_method,
        numeroCsv(montos.cash),
        numeroCsv(montos.card),
        numeroCsv(montos.transfer),
        numeroCsv(sale.discount_amount),
        numeroCsv(sale.total),
        STATUS_LABELS[sale.status] || sale.status,
        sale.has_invoice ? 'Si' : 'No',
      ];
    });

    // El nombre lleva el recorte para que dos descargas distintas no se pisen
    const partes = ['ventas'];
    if (dateFrom || dateTo) partes.push(`${dateFrom || 'inicio'}_a_${dateTo || 'hoy'}`);
    if (paymentFilter) partes.push(paymentFilter);

    descargarCsv(
      partes.join('_'),
      [
        'Comprobante', 'Fecha y hora', 'Cliente', 'Vendedor', 'Items', 'Pago',
        'Efectivo', 'Tarjeta', 'Transferencia', 'Descuento', 'Total', 'Estado', 'Facturada',
      ],
      filas,
    );
  };

  const sortedOwners = useMemo(() => {
    return [...owners]
      .map((o) => ({ value: o.id.toString(), label: `${o.first_name} ${o.last_name}` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [owners]);

  // Todas las ventas abiertas, en orden fijo. La actual se arma con lo que hay en
  // pantalla; las demas, con lo que quedo guardado.
  const tabsDeVentas = useMemo(() => {
    const ids = saleOrder.includes(currentSaleId) ? saleOrder : [...saleOrder, currentSaleId];
    const etiqueta = (ownerId: string) =>
      ownerId ? (sortedOwners.find((o) => o.value === ownerId)?.label || 'Cliente') : 'Sin cliente';

    return ids
      .map((id) => {
        if (id === currentSaleId) {
          return {
            id,
            label: newSaleOwnerId ? etiqueta(newSaleOwnerId) : 'Venta actual',
            count: newSaleItems.length,
          };
        }
        const parked = parkedSales.find((p) => p.id === id);
        if (!parked) return null;
        return { id, label: etiqueta(parked.ownerId), count: parked.items.length };
      })
      .filter((t): t is { id: number; label: string; count: number } => t !== null);
  }, [saleOrder, currentSaleId, parkedSales, newSaleOwnerId, newSaleItems.length, sortedOwners]);

  const filteredOwners = useMemo(() => {
    const trimmed = ownerSearch.trim();
    if (!trimmed) return sortedOwners;
    const normalize = (s: string) =>
      s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    const words = normalize(trimmed).split(/\s+/).filter(Boolean);
    return sortedOwners.filter((o) => {
      const label = normalize(o.label);
      return words.every((w) => label.includes(w));
    });
  }, [sortedOwners, ownerSearch]);

  const selectedOwnerLabel = useMemo(() => {
    if (!newSaleOwnerId) return 'Cliente ocasional';
    return sortedOwners.find((o) => o.value === newSaleOwnerId)?.label || 'Cliente ocasional';
  }, [newSaleOwnerId, sortedOwners]);

  // Stock lindo: 8 en vez de 8.00, 7,5 cuando hace falta
  const fmtStock = (n: number) => n.toLocaleString('es-AR', { maximumFractionDigits: 2 });

  // Componente de tarjeta de producto
  const ProductListRow = ({ product, onAdd }: { product: Product; onAdd: () => void }) => {
    const isService = product.is_service;
    // El fraccionado no tiene stock propio: lo que hay son las unidades que salen del
    // producto a granel. Antes se leia su current_stock (siempre 0) y figuraba "Sin
    // stock" aunque el granel estuviera lleno.
    const isFraccionado = !!(product.source_product_id && product.fraction_quantity);
    const parentKg = Number(product.source_current_stock ?? 0);
    const availableUnits = Number(product.available_units ?? 0);
    const stock = isFraccionado ? availableUnits : (product.current_stock ?? 0);
    const noStock = !isService && stock <= 0;

    return (
      <button
        onClick={onAdd}
        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-cream-50 ${
          noStock ? 'bg-orange-50/30' : ''
        }`}
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
          isService ? 'bg-purple-100 text-purple-600' : noStock ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'
        }`}>
          {isService ? <Wrench className="h-4 w-4" /> : <Package className="h-4 w-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-taupe-800 text-sm leading-snug break-words" title={product.name}>
            {isFraccionado && (
              <span className="inline-flex items-center px-1.5 py-0.5 mr-1.5 rounded text-[11px] font-normal bg-teal-100 text-teal-800 align-middle">
                Fraccionado
              </span>
            )}
            {product.name}
          </p>
          <div className="flex items-center gap-3 text-xs">
            {!isService && (
              <span className={noStock ? 'text-orange-600 font-medium' : 'text-taupe-500'}>
                {noStock
                  ? 'Sin stock'
                  : isFraccionado
                    ? `${fmtStock(availableUnits)} u. · ${fmtStock(parentKg)} kg a granel`
                    : `Stock: ${fmtStock(Number(stock))}`}
              </span>
            )}
            {product.code && (
              <span className="text-taupe-400 truncate">Cód: {product.code}</span>
            )}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-sm sm:text-base font-bold text-green-600 leading-tight whitespace-nowrap">
            {product.sale_price != null ? formatCurrency(product.sale_price) : '–'}
          </p>
          {/* Con precios por medio de pago se ven los tres niveles, no solo el de efectivo */}
          {priceByPayment && product.sale_price != null && (
            <>
              <p className="text-[11px] text-blue-600 leading-tight whitespace-nowrap">
                {formatCurrency(product.list_price ?? product.sale_price)} tarj.
              </p>
              <p className="text-[11px] text-purple-600 leading-tight whitespace-nowrap">
                {formatCurrency(product.installment_price ?? product.list_price ?? product.sale_price)} cuotas
              </p>
            </>
          )}
        </div>
        <div className="w-8 h-8 rounded-full bg-taupe-100 flex items-center justify-center flex-shrink-0">
          <Plus className="h-4 w-4 text-taupe-600" />
        </div>
      </button>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-taupe-800">Ventas</h1>
        <p className="text-taupe-500">Gestión de ventas y comandas</p>
      </div>

      {/* Banner Caja Cerrada */}
      {isCashClosed && (
        <Link
          to="/dashboard/cash-register"
          className="block bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-5 shadow-lg hover:shadow-xl transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Caja cerrada</h3>
                <p className="text-amber-100 text-sm">Debes abrir la caja antes de registrar ventas</p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg group-hover:bg-white/30 transition-all">
              <span className="text-white font-medium">Abrir Caja</span>
              <ArrowRight className="h-4 w-4 text-white group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </Link>
      )}

      {/* Tabs grandes y claros */}
      <div className="flex gap-3">
        <button
          onClick={() => setActiveTab('history')}
          className={`flex-1 py-4 px-6 rounded-xl font-medium transition-all ${
            activeTab === 'history'
              ? 'bg-taupe-700 text-white shadow-lg'
              : 'bg-white border-2 border-cream-200 text-taupe-600 hover:border-taupe-300'
          }`}
        >
          <Eye className="h-5 w-5 inline mr-2" />
          Historial
        </button>
        <button
          onClick={() => setActiveTab('new')}
          className={`flex-1 py-4 px-6 rounded-xl font-medium transition-all ${
            activeTab === 'new'
              ? 'bg-green-600 text-white shadow-lg'
              : 'bg-white border-2 border-cream-200 text-taupe-600 hover:border-green-300'
          }`}
        >
          <Plus className="h-5 w-5 inline mr-2" />
          Nueva Venta
        </button>
        <button
          onClick={() => setActiveTab('orders')}
          className={`flex-1 py-4 px-6 rounded-xl font-medium transition-all relative ${
            activeTab === 'orders'
              ? 'bg-orange-500 text-white shadow-lg'
              : 'bg-white border-2 border-cream-200 text-taupe-600 hover:border-orange-300'
          }`}
        >
          <ClipboardList className="h-5 w-5 inline mr-2" />
          Comandas
          {pendingOrders.length > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
              {pendingOrders.length}
            </span>
          )}
        </button>
      </div>

      {/* HISTORIAL */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-2xl border border-cream-200 p-6">
          {/* Filtros rápidos */}
          <div className="flex gap-2 mb-4">
            {(() => {
              // Usar fecha local en lugar de UTC
              const formatLocalDate = (date: Date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
              };
              const todayDate = new Date();
              const today = formatLocalDate(todayDate);
              const weekAgo = formatLocalDate(new Date(todayDate.getTime() - 7 * 24 * 60 * 60 * 1000));
              const monthStart = formatLocalDate(new Date(todayDate.getFullYear(), todayDate.getMonth(), 1));

              const isToday = dateFrom === today && dateTo === today;
              const isWeek = dateFrom === weekAgo && dateTo === today;
              const isMonth = dateFrom === monthStart && dateTo === today;
              const isAll = !dateFrom && !dateTo;

              return (
                <>
                  <button
                    onClick={() => {
                      setDateFrom(today);
                      setDateTo(today);
                    }}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      isToday
                        ? 'bg-taupe-600 text-white'
                        : 'bg-taupe-100 text-taupe-700 hover:bg-taupe-200'
                    }`}
                  >
                    Hoy
                  </button>
                  <button
                    onClick={() => {
                      setDateFrom(weekAgo);
                      setDateTo(today);
                    }}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      isWeek
                        ? 'bg-taupe-600 text-white'
                        : 'bg-taupe-100 text-taupe-700 hover:bg-taupe-200'
                    }`}
                  >
                    Última semana
                  </button>
                  <button
                    onClick={() => {
                      setDateFrom(monthStart);
                      setDateTo(today);
                    }}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      isMonth
                        ? 'bg-taupe-600 text-white'
                        : 'bg-taupe-100 text-taupe-700 hover:bg-taupe-200'
                    }`}
                  >
                    Este mes
                  </button>
                  <button
                    onClick={() => {
                      setPaymentFilter('');
                      setDateFrom('');
                      setDateTo('');
                    }}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      isAll
                        ? 'bg-taupe-600 text-white'
                        : 'bg-taupe-100 text-taupe-700 hover:bg-taupe-200'
                    }`}
                  >
                    Todo
                  </button>
                </>
              );
            })()}
          </div>

          {/* Filtros avanzados */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Select
              label="Método de pago"
              options={PAYMENT_METHOD_OPTIONS}
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value as PaymentMethod | '')}
            />
            <Input
              type="date"
              label="Desde"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <Input
              type="date"
              label="Hasta"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
            <div className="flex items-end">
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => {
                  setPaymentFilter('');
                  setDateFrom('');
                  setDateTo('');
                }}
              >
                <Filter className="h-4 w-4 mr-2" />
                Limpiar
              </Button>
            </div>
            <div className="flex items-end">
              <Button
                variant="secondary"
                className="w-full"
                onClick={descargarHistorial}
                disabled={sales.length === 0}
                title="Baja en Excel las ventas que se estan viendo, con los filtros puestos"
              >
                <Download className="h-4 w-4 mr-2" />
                Descargar
              </Button>
            </div>
          </div>

          {/* Resumen */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <div className="bg-green-50 rounded-xl p-3 sm:p-4 border border-green-200 min-w-0">
              <div className="flex items-center gap-2 text-green-700 mb-1">
                <Banknote className="h-5 w-5 flex-shrink-0" />
                <span className="font-medium truncate">Efectivo</span>
              </div>
              <p className="text-base sm:text-2xl font-bold text-green-800 leading-tight tabular-nums break-words">{formatCurrency(totals.cash)}</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-3 sm:p-4 border border-blue-200 min-w-0">
              <div className="flex items-center gap-2 text-blue-700 mb-1">
                <CreditCard className="h-5 w-5 flex-shrink-0" />
                <span className="font-medium truncate">Tarjeta</span>
              </div>
              <p className="text-base sm:text-2xl font-bold text-blue-800 leading-tight tabular-nums break-words">{formatCurrency(totals.card)}</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-3 sm:p-4 border border-purple-200 min-w-0">
              <div className="flex items-center gap-2 text-purple-700 mb-1">
                <ArrowRightLeft className="h-5 w-5 flex-shrink-0" />
                <span className="font-medium truncate">Transf.</span>
              </div>
              <p className="text-base sm:text-2xl font-bold text-purple-800 leading-tight tabular-nums break-words">{formatCurrency(totals.transfer)}</p>
            </div>
            <div className="bg-taupe-50 rounded-xl p-3 sm:p-4 border border-taupe-200 min-w-0">
              <div className="flex items-center gap-2 text-taupe-700 mb-1">
                <DollarSign className="h-5 w-5 flex-shrink-0" />
                <span className="font-medium truncate">Total</span>
              </div>
              <p className="text-base sm:text-2xl font-bold text-taupe-800 leading-tight tabular-nums break-words">{formatCurrency(totals.total)}</p>
            </div>
          </div>

          {/* Tabla */}
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-taupe-600 mx-auto"></div>
              <p className="mt-3 text-taupe-500">Cargando ventas...</p>
            </div>
          ) : sales.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="h-16 w-16 text-taupe-300 mx-auto mb-4" />
              <p className="text-taupe-600 font-medium">No hay ventas</p>
              <p className="text-taupe-400 text-sm">Las ventas realizadas aparecerán aquí</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-cream-200">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-taupe-600">Comprobante</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-taupe-600">Fecha</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-taupe-600">Cliente</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-taupe-600">Pago</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-taupe-600">Total</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-taupe-600">Estado</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-taupe-600">Ver</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-cream-100">
                  {sales.map((sale) => (
                    <tr key={sale.id} className="hover:bg-cream-50">
                      {/*
                        Las ventas con factura electronica se marcan solo con el color del
                        numero: alcanza para ubicarlas de un vistazo y no le canta a nadie
                        que mire la pantalla cuales se facturaron y cuales no.
                      */}
                      <td className="px-4 py-3 font-mono text-sm">
                        <span
                          className={sale.has_invoice ? 'text-blue-600 font-semibold' : undefined}
                          title={sale.has_invoice ? 'Tiene factura electrónica' : undefined}
                        >
                          {sale.receipt_number || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-taupe-600">{formatDate(sale.created_at)}</td>
                      <td className="px-4 py-3 text-sm">{sale.owner_name || 'Cliente ocasional'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-sm">
                          {PAYMENT_METHOD_ICONS[sale.payment_method]}
                          <span>{PAYMENT_METHOD_LABELS[sale.payment_method]}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-bold">{formatCurrency(sale.total)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[sale.status]}`}>
                          {STATUS_LABELS[sale.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleViewDetail(sale)}
                          className="p-2 text-taupe-500 hover:text-taupe-700 hover:bg-taupe-100 rounded-lg"
                          title="Ver detalle"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                        {sale.status === 'completed' && (
                          <button
                            onClick={() => setComprobanteSale(sale)}
                            className="p-2 text-taupe-500 hover:text-taupe-700 hover:bg-taupe-100 rounded-lg ml-1"
                            title="Comprobante o factura"
                          >
                            <FileText className="h-5 w-5" />
                          </button>
                        )}
                        {sale.status === 'completed' && isAdmin && (
                          <button
                            onClick={() => setCancelConfirm(sale)}
                            className="p-2 text-taupe-500 hover:text-red-600 hover:bg-red-50 rounded-lg ml-1"
                          >
                            <XCircle className="h-5 w-5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* NUEVA VENTA */}
      {activeTab === 'new' && (
        <div className="space-y-4">
          {/* Pestañas de ventas en paralelo */}
          <div className="bg-white rounded-2xl border border-cream-200 p-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-taupe-500 mr-1">Ventas:</span>
              {/*
                Una pestaña por venta abierta, en orden fijo. La activa se marca donde
                esta: antes el chip verde era siempre el primero y las demas se
                reordenaban al cambiar, asi que no se sabia en cual estabas parada.
              */}
              {tabsDeVentas.map((tab, i) => {
                const activa = tab.id === currentSaleId;
                return (
                  <div
                    key={tab.id}
                    className={`inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 rounded-lg text-sm ${
                      activa
                        ? 'bg-green-600 text-white font-medium ring-2 ring-green-300'
                        : 'border border-cream-300 bg-white text-taupe-700'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => handleSwitchToParked(tab.id)}
                      className={`inline-flex items-center gap-1.5 ${activa ? '' : 'hover:text-green-700'}`}
                      title={activa ? 'Estás en esta venta' : 'Cambiar a esta venta'}
                    >
                      {activa && <ShoppingBag className="h-4 w-4" />}
                      <span className={activa ? '' : 'text-taupe-400'}>{i + 1}.</span>
                      <span>{tab.label}</span>
                      {tab.count > 0 && (
                        <span
                          className={`text-xs rounded-full px-1.5 ${
                            activa ? 'bg-white/25' : 'bg-cream-100'
                          }`}
                        >
                          {tab.count}
                        </span>
                      )}
                    </button>
                    {tabsDeVentas.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleCloseParked(tab.id)}
                        className={activa ? 'text-white/70 hover:text-white' : 'text-taupe-400 hover:text-red-500'}
                        title="Descartar"
                      >
                        <XCircle className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
              {/* Abrir otra venta en paralelo */}
              <button
                type="button"
                onClick={handleNewParallelSale}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm border border-dashed border-taupe-300 text-taupe-600 hover:bg-cream-50"
                title="Guardar la venta actual en espera y abrir otra"
              >
                <Plus className="h-4 w-4" /> Nueva
              </button>
            </div>
            {parkedSales.length > 0 && (
              <p className="text-xs text-taupe-400 mt-2">
                Estás en la venta {tabsDeVentas.findIndex((t) => t.id === currentSaleId) + 1} de{' '}
                {tabsDeVentas.length}. Cada una se guarda sola al cambiar de pestaña.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Productos - ocupa más espacio */}
          <div className="lg:col-span-3 bg-white rounded-2xl border border-cream-200 p-5">
            <h3 className="text-lg font-semibold text-taupe-800 mb-4 flex items-center gap-2">
              <LayoutGrid className="h-5 w-5 text-taupe-500" />
              Productos
            </h3>

            {/* Búsqueda */}
            <div className="relative mb-4">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-taupe-400" />
              <input
                type="text"
                placeholder="Buscar por nombre o código..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-cream-200 rounded-xl text-base focus:outline-none focus:border-taupe-400"
              />
            </div>

            {/* Categorías y botón Otro */}
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => setCategoryFilter('all')}
                className={`px-4 py-2 rounded-xl font-medium transition-all ${
                  categoryFilter === 'all'
                    ? 'bg-taupe-700 text-white'
                    : 'bg-cream-100 text-taupe-600 hover:bg-cream-200'
                }`}
              >
                Todos
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setCategoryFilter(String(cat.id))}
                  className={`px-4 py-2 rounded-xl font-medium transition-all ${
                    categoryFilter === String(cat.id)
                      ? 'bg-taupe-700 text-white'
                      : 'bg-cream-100 text-taupe-600 hover:bg-cream-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
              {/* Botón Otro - se convierte en inline form cuando está activo */}
              {!showCustomItemForm ? (
                <button
                  onClick={() => setShowCustomItemForm(true)}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all bg-purple-50 text-purple-600 hover:bg-purple-100 border border-purple-200 ml-auto"
                >
                  <Plus className="h-3.5 w-3.5 inline mr-1" />
                  Otro
                </button>
              ) : (
                <div className="flex items-center gap-2 ml-auto bg-purple-50 rounded-lg px-2 py-1 border border-purple-200">
                  <input
                    type="text"
                    placeholder="Nombre"
                    value={customItemName}
                    onChange={(e) => setCustomItemName(e.target.value)}
                    className="w-28 px-2 py-1 text-sm border border-purple-200 rounded focus:outline-none focus:border-purple-400 bg-white"
                    autoFocus
                  />
                  <input
                    type="number"
                    placeholder="$"
                    min="0"
                    step="0.01"
                    value={customItemPrice}
                    onChange={(e) => setCustomItemPrice(e.target.value)}
                    className="w-20 px-2 py-1 text-sm border border-purple-200 rounded focus:outline-none focus:border-purple-400 bg-white"
                  />
                  <button
                    onClick={handleAddCustomItem}
                    disabled={!customItemName.trim() || !customItemPrice}
                    className="p-1.5 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Agregar"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      setShowCustomItemForm(false);
                      setCustomItemName('');
                      setCustomItemPrice('');
                    }}
                    className="p-1 text-purple-400 hover:text-purple-600"
                    title="Cancelar"
                  >
                    <XCircle className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Contador de resultados */}
            {!isLoadingProducts && filteredProducts.length > 0 && (
              <p className="text-xs text-taupe-500 mb-2">
                {filteredProducts.length} producto{filteredProducts.length !== 1 ? 's' : ''}
                {productSearch.length >= 2 && ` para "${productSearch}"`}
              </p>
            )}

            {/* El granel no se vende: se avisa para que no parezca que falta el producto */}
            {!isLoadingProducts && hiddenBulkMatches.length > 0 && (
              <div className="mb-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                {hiddenBulkMatches.length === 1 ? (
                  <>
                    <strong>{hiddenBulkMatches[0].name}</strong> es un producto a granel, así que no
                    se vende directo: elegí el paquete fraccionado que corresponda.
                  </>
                ) : (
                  <>
                    Hay {hiddenBulkMatches.length} productos a granel que coinciden con la búsqueda.
                    No se venden directo: elegí el paquete fraccionado que corresponda.
                  </>
                )}
              </div>
            )}

            {/* Lista de productos */}
            <div className="h-[550px] overflow-y-auto pr-2 border border-cream-200 rounded-xl bg-white">
              {isLoadingProducts ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-taupe-600"></div>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-taupe-400">
                  <Package className="h-16 w-16 mb-3" />
                  <p className="font-medium">No hay productos</p>
                  {productSearch && (
                    <p className="text-xs mt-1">para "{productSearch}"</p>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-cream-100">
                  {filteredProducts.map((product) => (
                    <ProductListRow
                      key={product.id}
                      product={product}
                      onAdd={() => handleAddProduct(product)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Carrito */}
          <div className="lg:col-span-2 bg-gradient-to-b from-cream-50 to-cream-100 rounded-2xl border border-cream-200 p-5 flex flex-col">
            <h3 className="text-lg font-semibold text-taupe-800 mb-4 flex items-center gap-2">
              <ShoppingBag className="h-5 w-5 text-green-600" />
              Carrito
              {newSaleItems.length > 0 && (
                <span className="ml-auto bg-green-600 text-white text-sm px-3 py-1 rounded-full">
                  {newSaleItems.length}
                </span>
              )}
            </h3>

            {/* Items del carrito */}
            <div className="flex-1 overflow-y-auto mb-4">
              {newSaleItems.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-taupe-400 py-8">
                  <ShoppingBag className="h-16 w-16 mb-3 opacity-50" />
                  <p className="font-medium">Carrito vacío</p>
                  <p className="text-sm">Selecciona productos</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {newSaleItems.map((item) => {
                    // Item personalizado (sin producto)
                    if (!item.product) {
                      const price = item.customPrice ?? 0;
                      return (
                        <div key={item.id} className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs bg-purple-200 text-purple-700 px-2 py-0.5 rounded-full">Otro</span>
                              <p className="font-medium text-taupe-800">{item.customName}</p>
                            </div>
                            <button
                              onClick={() => handleRemoveItem(item.id)}
                              className="p-1 text-taupe-400 hover:text-red-500 hover:bg-red-50 rounded flex-shrink-0"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-taupe-500">{formatCurrency(price)} c/u</p>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                                className="w-8 h-8 rounded-lg bg-purple-100 hover:bg-purple-200 font-bold"
                              >
                                -
                              </button>
                              <span className="w-8 text-center font-bold">{item.quantity}</span>
                              <button
                                onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                                className="w-8 h-8 rounded-lg bg-purple-100 hover:bg-purple-200 font-bold"
                              >
                                +
                              </button>
                            </div>
                            <p className="font-bold text-green-600 text-lg">
                              {formatCurrency(lineTotalFor(item, currentTier))}
                            </p>
                          </div>
                          {renderLineDiscount(item)}
                        </div>
                      );
                    }

                    // Producto normal o por ml
                    const isMlProduct = item.product.sell_by_ml;
                    const needsMlQuantity = isMlProduct && (!item.mlQuantity || item.mlQuantity <= 0);
                    const price = isMlProduct
                      ? ((item.product.price_per_ml ?? 0) * (item.mlQuantity ?? 0))
                      : (item.customPrice ?? item.product.sale_price ?? 0);
                    const needsPrice = item.product.is_service && item.product.sale_price == null && !item.customPrice;

                    return (
                      <div key={item.id} className={`bg-white rounded-xl p-4 border ${needsPrice || needsMlQuantity ? 'border-orange-300' : 'border-cream-200'}`}>
                        {/* Nombre del producto */}
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            {isMlProduct && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">ml</span>
                            )}
                            <p className="font-medium text-taupe-800">{item.product.name}</p>
                          </div>
                          <button
                            onClick={() => handleRemoveItem(item.id)}
                            className="p-1 text-taupe-400 hover:text-red-500 hover:bg-red-50 rounded flex-shrink-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Input de ml para productos por ml */}
                        {isMlProduct && (
                          <div className="mb-3 flex items-center gap-2">
                            <span className="text-sm text-taupe-500">Cantidad:</span>
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              placeholder="0"
                              value={item.mlQuantity ?? ''}
                              onChange={(e) => handleUpdateMlQuantity(item.id, e.target.value ? parseFloat(e.target.value) : undefined)}
                              className={`w-20 px-3 py-1 border rounded-lg ${needsMlQuantity ? 'border-orange-400 bg-orange-50' : 'border-cream-300'}`}
                            />
                            <span className="text-sm text-taupe-500">ml</span>
                            <span className="text-xs text-taupe-400 ml-auto">
                              ({formatCurrency(item.product.price_per_ml ?? 0)}/ml)
                            </span>
                          </div>
                        )}

                        {/* Precio, cantidad y subtotal */}
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-taupe-500">
                            {isMlProduct ? `${item.mlQuantity ?? 0} ml` : formatCurrency(price)} c/u
                          </p>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                              className="w-8 h-8 rounded-lg bg-cream-100 hover:bg-cream-200 font-bold"
                            >
                              -
                            </button>
                            <span className="w-8 text-center font-bold">{item.quantity}</span>
                            <button
                              onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                              className="w-8 h-8 rounded-lg bg-cream-100 hover:bg-cream-200 font-bold"
                            >
                              +
                            </button>
                          </div>
                          <p className="font-bold text-green-600 text-lg">
                            {formatCurrency(lineTotalFor(item, currentTier))}
                          </p>
                        </div>

                        {/* Precio editable para servicios */}
                        {item.product.is_service && !isMlProduct && (
                          <div className="mt-3 flex items-center gap-2">
                            <span className="text-sm text-taupe-500">Precio:</span>
                            <input
                              type="number"
                              min="0"
                              placeholder={item.product.sale_price?.toString() || "0"}
                              value={item.customPrice ?? ''}
                              onChange={(e) => handleUpdatePrice(item.id, e.target.value ? parseFloat(e.target.value) : undefined)}
                              className={`w-28 px-3 py-1 border rounded-lg ${needsPrice ? 'border-orange-400 bg-orange-50' : 'border-cream-300'}`}
                            />
                          </div>
                        )}

                        {renderLineDiscount(item)}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Cliente y método de pago */}
            <div className="space-y-4 mb-4">
              <div className="relative" ref={ownerDropdownRef}>
                <label className="block text-sm font-medium text-taupe-700 mb-1">Cliente</label>
                <div
                  className="w-full px-3 py-2 border border-cream-300 rounded-xl cursor-pointer flex items-center justify-between bg-white hover:border-taupe-400 transition-colors"
                  onClick={() => setShowOwnerDropdown(!showOwnerDropdown)}
                >
                  <span className={newSaleOwnerId ? 'text-taupe-800' : 'text-taupe-400'}>
                    {selectedOwnerLabel}
                  </span>
                  <Search className="h-4 w-4 text-taupe-400" />
                </div>
                {showOwnerDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-cream-200 rounded-xl shadow-lg max-h-60 overflow-hidden">
                    <div className="p-2 border-b border-cream-100">
                      <input
                        type="text"
                        value={ownerSearch}
                        onChange={(e) => setOwnerSearch(e.target.value)}
                        placeholder="Buscar cliente..."
                        className="w-full px-3 py-2 border border-cream-200 rounded-lg text-sm focus:ring-2 focus:ring-green-400 focus:border-green-400 outline-none"
                        autoFocus
                      />
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      <button
                        type="button"
                        onClick={() => { setNewSaleOwnerId(''); setOwnerSearch(''); setShowOwnerDropdown(false); }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-cream-50 ${!newSaleOwnerId ? 'bg-green-50 text-green-700 font-medium' : 'text-taupe-600'}`}
                      >
                        Cliente ocasional
                      </button>
                      {filteredOwners.map((o) => (
                        <button
                          key={o.value}
                          type="button"
                          onClick={() => { setNewSaleOwnerId(o.value); setOwnerSearch(''); setShowOwnerDropdown(false); }}
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-cream-50 ${newSaleOwnerId === o.value ? 'bg-green-50 text-green-700 font-medium' : 'text-taupe-700'}`}
                        >
                          {o.label}
                        </button>
                      ))}
                      {filteredOwners.length === 0 && ownerSearch.trim() && (
                        <p className="px-3 py-2 text-sm text-taupe-400 text-center">No se encontraron clientes</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-taupe-700 mb-2">Método de pago</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['cash', 'card', 'transfer'] as PaymentMethod[]).map((method) => (
                    <button
                      key={method}
                      onClick={() => setNewSalePayment(method)}
                      className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-all ${
                        newSalePayment === method
                          ? 'bg-taupe-700 border-taupe-700 text-white'
                          : 'bg-white border-cream-200 text-taupe-600 hover:border-taupe-300'
                      }`}
                    >
                      {PAYMENT_METHOD_ICONS[method]}
                      <span className="text-xs font-medium">{PAYMENT_METHOD_LABELS[method]}</span>
                    </button>
                  ))}
                </div>

                {/* Pago dividido: parte por un medio y parte por otro */}
                <label className="flex items-center gap-2 cursor-pointer mt-3">
                  <input
                    type="checkbox"
                    checked={splitMode}
                    onChange={(e) => {
                      setSplitMode(e.target.checked);
                      if (!e.target.checked) {
                        setSplitCash(''); setSplitCard(''); setSplitTransfer('');
                      }
                    }}
                    className="h-4 w-4 rounded border-cream-300"
                  />
                  <span className="text-sm text-taupe-700">
                    Pago dividido
                    <span className="block text-xs text-taupe-400">
                      Cuando paga una parte en efectivo y otra por otro medio.
                    </span>
                  </span>
                </label>

                {splitMode && (
                  <div className="mt-3 p-3 rounded-xl border border-cream-200 bg-cream-50 space-y-3">
                    <p className="text-xs text-taupe-500">
                      Cargá cuánto entró por cada medio. Tienen que sumar el total.
                    </p>
                    {([
                      { label: 'Efectivo', value: splitCash, set: setSplitCash },
                      { label: 'Tarjeta', value: splitCard, set: setSplitCard },
                      { label: 'Transferencia', value: splitTransfer, set: setSplitTransfer },
                    ]).map((campo) => (
                      <div key={campo.label} className="flex items-center gap-2">
                        <span className="w-28 text-sm text-taupe-600">{campo.label}</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={campo.value}
                          onChange={(e) => campo.set(e.target.value.replace(/[^\d.,]/g, ''))}
                          placeholder="0"
                          className="flex-1 px-3 py-1.5 border border-cream-300 rounded-lg text-sm text-right"
                        />
                      </div>
                    ))}
                    <div className="border-t border-cream-200 pt-2 text-sm flex justify-between">
                      <span className="text-taupe-600">Cargado</span>
                      <span className="font-medium">{formatCurrency(splitSum)}</span>
                    </div>
                    {!splitCierra && (
                      <p className="text-sm text-red-600">
                        {splitFalta > 0
                          ? `Faltan ${formatCurrency(splitFalta)} para llegar al total.`
                          : `Sobran ${formatCurrency(Math.abs(splitFalta))} sobre el total.`}
                      </p>
                    )}
                    {splitCierra && splitMediosUsados < 2 && (
                      <p className="text-sm text-amber-700">
                        Entró todo por un solo medio: destildá "Pago dividido" y elegilo arriba.
                      </p>
                    )}
                  </div>
                )}

                {/* Sub-opciones de tarjeta (débito/crédito + cuotas) - solo tracking */}
                {newSalePayment === 'card' && (
                  <div className="mt-3 p-3 rounded-xl border border-cream-200 bg-cream-50 space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setCardType('debit')}
                        className={`py-2 rounded-lg border text-sm font-medium transition-colors ${
                          cardType === 'debit' ? 'bg-blue-100 border-blue-400 text-blue-700' : 'bg-white border-cream-200 text-taupe-600'
                        }`}
                      >
                        Débito
                      </button>
                      <button
                        type="button"
                        onClick={() => setCardType('credit')}
                        className={`py-2 rounded-lg border text-sm font-medium transition-colors ${
                          cardType === 'credit' ? 'bg-blue-100 border-blue-400 text-blue-700' : 'bg-white border-cream-200 text-taupe-600'
                        }`}
                      >
                        Crédito
                      </button>
                    </div>
                    {cardType === 'credit' && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-taupe-600">Cuotas:</span>
                        <input
                          type="number"
                          min="1"
                          max="24"
                          value={cardInstallments}
                          onChange={(e) => setCardInstallments(Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-20 px-3 py-1.5 border border-cream-300 rounded-lg text-sm"
                        />
                        <span className="text-xs text-taupe-400">(solo registro interno, no cambia el monto)</span>
                      </div>
                    )}
                    <label className="flex items-start gap-2 cursor-pointer pt-1">
                      <input
                        type="checkbox"
                        checked={chargeAsCash}
                        onChange={(e) => setChargeAsCash(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-cream-300"
                      />
                      <span className="text-sm text-taupe-700">
                        Cobrar al precio de efectivo
                        <span className="block text-xs text-taupe-400">
                          Sin el recargo de tarjeta, para esta venta.
                        </span>
                      </span>
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Total y botón */}
            <div className="bg-white rounded-xl p-4 border-2 border-green-200 space-y-3">
              {/* Descuento */}
              <div className="border rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium text-gray-600">Descuento (opcional)</p>
                <div className="flex gap-2">
                  <button
                    className={`flex-1 py-1.5 text-sm rounded-lg border font-medium transition-colors ${directDiscountType === 'percentage' ? 'bg-green-100 border-green-400 text-green-700' : 'bg-white border-gray-200 text-gray-500'}`}
                    onClick={() => { setDirectDiscountType('percentage'); setDirectDiscountValue(''); }}
                  >% Porcentaje</button>
                  <button
                    className={`flex-1 py-1.5 text-sm rounded-lg border font-medium transition-colors ${directDiscountType === 'fixed' ? 'bg-green-100 border-green-400 text-green-700' : 'bg-white border-gray-200 text-gray-500'}`}
                    onClick={() => { setDirectDiscountType('fixed'); setDirectDiscountValue(''); }}
                  >$ Monto fijo</button>
                </div>
                <input
                  type="number"
                  min="0"
                  placeholder={directDiscountType === 'percentage' ? 'Ej: 10 (10%)' : 'Ej: 5000'}
                  value={directDiscountValue}
                  onChange={(e) => setDirectDiscountValue(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
                />
              </div>

              {/* Total */}
              <div className="space-y-1">
                {directDiscountNum > 0 && (
                  <div className="flex justify-between items-center text-sm text-gray-400">
                    <span>Subtotal:</span>
                    <span className="line-through">{formatCurrency(newSaleTotal)}</span>
                  </div>
                )}
                {directDiscountNum > 0 && (
                  <div className="flex justify-between items-center text-sm text-green-600 font-medium">
                    <span>Descuento:</span>
                    <span>-{directDiscountType === 'percentage' ? `${directDiscountNum}%` : formatCurrency(directDiscountNum)}</span>
                  </div>
                )}
                {/* Botón de redondeo en efectivo (solo si la vete lo activó y el pago es efectivo) */}
                {canRound && (
                  <button
                    type="button"
                    onClick={() => setApplyRounding((v) => !v)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      roundingActive
                        ? 'bg-amber-100 border-amber-400 text-amber-800'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span>{roundingActive ? '✓ Redondeo aplicado' : 'Aplicar redondeo'}</span>
                    <span>{roundingActive ? formatCurrency(roundedTotal) : `→ ${formatCurrency(roundedTotal)}`}</span>
                  </button>
                )}
                {roundingActive && (
                  <div className="flex justify-between items-center text-sm text-amber-700 font-medium">
                    <span>Redondeo:</span>
                    <span>{roundingAdjustment >= 0 ? '+' : '−'}{formatCurrency(Math.abs(roundingAdjustment))}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-taupe-600 font-medium">Total:</span>
                  <span className="text-3xl font-bold text-green-600">{formatCurrency(finalChargeTotal)}</span>
                </div>
              </div>

              {hasItemsWithoutPrice && (
                <p className="text-sm text-orange-600 text-center">
                  Ingrese el precio de los servicios
                </p>
              )}
              <Button
                className="w-full py-4 text-lg font-bold bg-green-600 hover:bg-green-700 rounded-xl"
                disabled={newSaleItems.length === 0 || createSale.isPending || hasItemsWithoutPrice || !splitValido}
                onClick={() => setShowConfirmSale(true)}
              >
                <Check className="h-5 w-5 mr-2" />
                Cobrar
              </Button>
            </div>
          </div>
          </div>
        </div>
      )}

      {/* COMANDAS */}
      {activeTab === 'orders' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Lista de comandas */}
          <div className="bg-white rounded-2xl border border-cream-200 p-5">
            <h3 className="text-lg font-semibold text-taupe-800 mb-4 flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-orange-500" />
              Comandas Pendientes
              {pendingOrders.length > 0 && (
                <span className="ml-2 bg-orange-500 text-white text-sm px-3 py-1 rounded-full">
                  {pendingOrders.length}
                </span>
              )}
            </h3>

            {isLoadingOrders ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500 mx-auto"></div>
              </div>
            ) : pendingOrders.length === 0 ? (
              <div className="text-center py-12 text-taupe-400">
                <ClipboardList className="h-16 w-16 mx-auto mb-3 opacity-50" />
                <p className="font-medium">Sin comandas</p>
                <p className="text-sm">Las comandas pendientes aparecerán aquí</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingOrders.map((order) => (
                  <button
                    key={order.id}
                    onClick={() => handleSelectOrder(order)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      selectedOrder?.id === order.id
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-cream-200 bg-white hover:border-orange-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          {order.patient_name && (
                            <span className="flex items-center gap-1 font-medium text-taupe-800">
                              <Dog className="h-4 w-4 text-orange-500" />
                              {order.patient_name}
                            </span>
                          )}
                          {order.owner_name && (
                            <span className="flex items-center gap-1 text-sm text-taupe-500">
                              <User className="h-3 w-3" />
                              {order.owner_name}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-taupe-400">
                          <Clock className="h-3 w-3" />
                          {formatDate(order.created_at)}
                          <span>·</span>
                          <span>{order.items.length} items</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-orange-600">
                          {formatCurrency(order.total)}
                        </p>
                        <ChevronRight className="h-5 w-5 text-taupe-400 ml-auto" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Detalle de comanda */}
          <div className="bg-gradient-to-b from-cream-50 to-cream-100 rounded-2xl border border-cream-200 p-5">
            {!selectedOrder ? (
              <div className="h-full flex flex-col items-center justify-center text-taupe-400 py-12">
                <ClipboardList className="h-20 w-20 mb-4 opacity-50" />
                <p className="text-lg font-medium">Selecciona una comanda</p>
                <p className="text-sm">Haz clic en una comanda para ver detalles</p>
              </div>
            ) : (
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-taupe-800">
                      Comanda #{selectedOrder.id}
                    </h3>
                    <p className="text-sm text-taupe-500">
                      {selectedOrder.patient_name} - {selectedOrder.owner_name}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedOrder(null)}
                    className="p-2 text-taupe-400 hover:text-taupe-600 hover:bg-white rounded-lg"
                  >
                    <XCircle className="h-5 w-5" />
                  </button>
                </div>

                {/* Items de la comanda */}
                <div className="bg-white rounded-xl p-4 mb-4 border border-cream-200">
                  <h4 className="text-sm font-medium text-taupe-600 mb-3 flex items-center gap-2">
                    <ClipboardList className="h-4 w-4 text-orange-500" />
                    Items de la comanda
                  </h4>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {selectedOrder.items.map((item) => {
                      const product = allProducts.find(p => p.id === item.product_id);
                      let price: number;
                      if (orderConRecargo) {
                        // Si es producto por ML, aplicar recargo configurable
                        if ((item.ml_quantity && item.ml_quantity > 0) || product?.sell_by_ml) {
                          price = Math.round(item.unit_price * cardSurchargeMultiplier);
                        } else {
                          price = product?.list_price ?? item.unit_price;
                        }
                      } else {
                        price = item.unit_price;
                      }
                      return (
                        <div key={item.id} className="flex justify-between text-sm">
                          <span>{item.product_name} x{item.quantity}</span>
                          <span className="font-medium">{formatCurrency(price * item.quantity)}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-2 pt-2 border-t border-cream-200 flex justify-end">
                    <span className="text-sm text-taupe-600">
                      Subtotal: <span className="font-bold">{formatCurrency(orderTotal)}</span>
                    </span>
                  </div>
                </div>

                {/* Items adicionales agregados - Sección separada y visible ARRIBA */}
                {orderExtraItems.length > 0 && (
                  <div className="bg-green-50 rounded-xl p-4 mb-4 border-2 border-green-200">
                    <h4 className="text-sm font-medium text-green-700 mb-3 flex items-center gap-2">
                      <ShoppingBag className="h-4 w-4" />
                      Productos Adicionales ({orderExtraItems.length})
                    </h4>
                    <div className="space-y-2">
                      {orderExtraItems.map((item, index) => {
                        // Item customizado (sin producto)
                        if (!item.product) {
                          const basePrice = item.customPrice ?? 0;
                          const price = (orderConRecargo)
                            ? Math.round(basePrice * cardSurchargeMultiplier)
                            : basePrice;
                          return (
                            <div key={`custom-${index}`} className="flex items-center gap-2 text-sm bg-purple-100 rounded-lg px-3 py-2">
                              <span className="text-xs bg-purple-300 text-purple-800 px-2 py-0.5 rounded font-medium">Otro</span>
                              <span className="flex-1 truncate font-medium">{item.customName}</span>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleUpdateCustomExtraQuantity(index, item.quantity - 1)}
                                  className="w-7 h-7 rounded-lg bg-purple-200 text-sm font-bold hover:bg-purple-300"
                                >
                                  -
                                </button>
                                <span className="w-8 text-center font-bold">{item.quantity}</span>
                                <button
                                  onClick={() => handleUpdateCustomExtraQuantity(index, item.quantity + 1)}
                                  className="w-7 h-7 rounded-lg bg-purple-200 text-sm font-bold hover:bg-purple-300"
                                >
                                  +
                                </button>
                              </div>
                              <span className="font-bold w-24 text-right text-purple-700">{formatCurrency(price * item.quantity)}</span>
                              <button onClick={() => handleRemoveCustomExtraItem(index)} className="p-1 text-red-500 hover:bg-red-100 rounded">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          );
                        }
                        // Producto normal
                        const product = item.product;
                        const price = item.customPrice ?? (
                          (orderConRecargo)
                            ? (product.list_price ?? product.sale_price ?? 0)
                            : (product.sale_price ?? 0)
                        );
                        return (
                          <div key={product.id} className="text-sm bg-white rounded-lg px-3 py-2 space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="flex-1 truncate font-medium">{product.name}</span>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleUpdateExtraQuantity(product.id, item.quantity - 1)}
                                  className="w-7 h-7 rounded-lg bg-cream-200 text-sm font-bold hover:bg-cream-300"
                                >
                                  -
                                </button>
                                <span className="w-8 text-center font-bold">{item.quantity}</span>
                                <button
                                  onClick={() => handleUpdateExtraQuantity(product.id, item.quantity + 1)}
                                  className="w-7 h-7 rounded-lg bg-cream-200 text-sm font-bold hover:bg-cream-300"
                                >
                                  +
                                </button>
                              </div>
                              <span className="font-bold w-24 text-right text-green-700">{formatCurrency(price * item.quantity)}</span>
                              <button onClick={() => handleRemoveExtraItem(product.id)} className="p-1 text-red-500 hover:bg-red-100 rounded">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                            {product.is_service && (
                              <div className="flex items-center gap-2 pl-1">
                                <span className="text-xs text-taupe-400">Precio:</span>
                                <input
                                  type="number"
                                  min="0"
                                  placeholder={product.sale_price?.toString() || "0"}
                                  value={item.customPrice ?? ''}
                                  onChange={(e) => handleUpdateExtraPrice(product.id, e.target.value ? parseFloat(e.target.value) : undefined)}
                                  className="w-24 px-2 py-1 text-xs border border-cream-300 rounded-lg focus:ring-1 focus:ring-orange-400 focus:border-orange-400 outline-none"
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-3 pt-2 border-t border-green-300 flex justify-end">
                      <span className="text-sm text-green-700">
                        Subtotal adicionales: <span className="font-bold text-green-800">{formatCurrency(extraItemsTotal)}</span>
                      </span>
                    </div>
                  </div>
                )}

                {/* Agregar productos extra */}
                <div className="bg-white rounded-xl p-4 mb-4 border border-cream-200 flex-1 overflow-hidden flex flex-col">
                  <h4 className="text-sm font-medium text-taupe-600 mb-3 flex items-center gap-2">
                    <Plus className="h-4 w-4 text-green-500" />
                    Agregar productos
                  </h4>

                  {/* Búsqueda */}
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-taupe-400" />
                    <input
                      type="text"
                      placeholder="Buscar..."
                      value={orderProductSearch}
                      onChange={(e) => setOrderProductSearch(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-cream-200 rounded-lg text-sm"
                    />
                  </div>

                  {/* Categorías y botón Otro */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <button
                      onClick={() => setOrderCategoryFilter('all')}
                      className={`px-3 py-1 rounded-lg text-sm ${
                        orderCategoryFilter === 'all' ? 'bg-taupe-700 text-white' : 'bg-cream-100 text-taupe-600'
                      }`}
                    >
                      Todos
                    </button>
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => setOrderCategoryFilter(String(cat.id))}
                        className={`px-3 py-1 rounded-lg text-sm ${
                          orderCategoryFilter === String(cat.id) ? 'bg-taupe-700 text-white' : 'bg-cream-100 text-taupe-600'
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                    {/* Botón Otro para items personalizados */}
                    {!showOrderCustomItemForm ? (
                      <button
                        onClick={() => setShowOrderCustomItemForm(true)}
                        className="px-3 py-1 rounded-lg text-sm font-medium bg-purple-50 text-purple-600 hover:bg-purple-100 border border-purple-200 ml-auto"
                      >
                        <Plus className="h-3 w-3 inline mr-1" />
                        Otro
                      </button>
                    ) : (
                      <div className="flex items-center gap-2 ml-auto bg-purple-50 rounded-lg px-2 py-1 border border-purple-200">
                        <input
                          type="text"
                          placeholder="Nombre"
                          value={orderCustomItemName}
                          onChange={(e) => setOrderCustomItemName(e.target.value)}
                          className="w-24 px-2 py-1 text-xs border border-purple-200 rounded focus:outline-none focus:border-purple-400 bg-white"
                          autoFocus
                        />
                        <input
                          type="number"
                          placeholder="$"
                          min="0"
                          step="0.01"
                          value={orderCustomItemPrice}
                          onChange={(e) => setOrderCustomItemPrice(e.target.value)}
                          className="w-16 px-2 py-1 text-xs border border-purple-200 rounded focus:outline-none focus:border-purple-400 bg-white"
                        />
                        <button
                          onClick={handleAddOrderCustomItem}
                          disabled={!orderCustomItemName.trim() || !orderCustomItemPrice}
                          className="p-1 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Agregar"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => {
                            setShowOrderCustomItemForm(false);
                            setOrderCustomItemName('');
                            setOrderCustomItemPrice('');
                          }}
                          className="p-1 text-purple-400 hover:text-purple-600"
                          title="Cancelar"
                        >
                          <XCircle className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Productos */}
                  <div className="flex-1 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-2">
                      {filteredOrderProducts.slice(0, 20).map((product) => (
                        <button
                          key={product.id}
                          onClick={() => handleAddExtraProduct(product)}
                          className="p-2 bg-cream-50 hover:bg-cream-100 rounded-lg text-left min-w-0"
                        >
                          <p className="text-sm font-medium leading-snug break-words" title={product.name}>{product.name}</p>
                          <p className="text-sm text-green-600 font-bold">
                            {formatCurrency(product.sale_price ?? 0)}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>

                </div>

                {/* Método de pago */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-taupe-600 mb-2">Método de pago</label>
                  <div className="grid grid-cols-3 gap-2">
                    {(['cash', 'card', 'transfer'] as PaymentMethod[]).map((method) => (
                      <button
                        key={method}
                        onClick={() => setOrderPaymentMethod(method)}
                        className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 ${
                          orderPaymentMethod === method
                            ? 'bg-orange-500 border-orange-500 text-white'
                            : 'bg-white border-cream-200 text-taupe-600'
                        }`}
                      >
                        {PAYMENT_METHOD_ICONS[method]}
                        <span className="text-xs">{PAYMENT_METHOD_LABELS[method]}</span>
                      </button>
                    ))}
                  </div>
                  {orderPaymentMethod === 'card' && (
                    <label className="flex items-start gap-2 cursor-pointer mt-3">
                      <input
                        type="checkbox"
                        checked={orderChargeAsCash}
                        onChange={(e) => setOrderChargeAsCash(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-cream-300"
                      />
                      <span className="text-sm text-taupe-700">
                        Cobrar al precio de efectivo
                        <span className="block text-xs text-taupe-400">
                          Sin el recargo de tarjeta, para esta comanda.
                        </span>
                      </span>
                    </label>
                  )}
                </div>

                {/* Total y cobrar */}
                <div className="bg-white rounded-xl p-4 border-2 border-orange-200">
                  {orderExtraItems.length > 0 && (
                    <div className="text-sm text-taupe-500 mb-2 space-y-1">
                      <div className="flex justify-between">
                        <span>Comanda:</span>
                        <span>{formatCurrency(orderTotal)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Adicionales:</span>
                        <span>{formatCurrency(extraItemsTotal)}</span>
                      </div>
                    </div>
                  )}
                  <div className="flex justify-between items-center mb-4">
                    <span className="font-medium text-taupe-600">Total:</span>
                    <span className="text-3xl font-bold text-orange-600">
                      {formatCurrency(orderTotalWithExtras)}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      className="py-4"
                      onClick={() => {
                        if (tenant && selectedOrder) generateServiceOrderReceipt(selectedOrder, tenant);
                      }}
                      title="Descargar PDF"
                    >
                      <Download className="h-5 w-5" />
                    </Button>
                    <Button
                      className="flex-1 py-4 text-lg font-bold bg-orange-500 hover:bg-orange-600 rounded-xl"
                      disabled={chargeOrder.isPending || createSale.isPending}
                      onClick={() => setIsChargeModalOpen(true)}
                    >
                      <Check className="h-5 w-5 mr-2" />
                      {chargeOrder.isPending ? 'Procesando...' : 'Cobrar'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de detalle de venta */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedSale(null);
        }}
        title={`Venta ${saleDetail?.receipt_number || ''}`}
      >
        {saleDetail && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-taupe-500">Fecha</p>
                <p className="font-medium">{formatDate(saleDetail.created_at)}</p>
              </div>
              <div>
                <p className="text-sm text-taupe-500">Tipo</p>
                <p className="font-medium">{TYPE_LABELS[saleDetail.sale_type]}</p>
              </div>
              <div>
                <p className="text-sm text-taupe-500">Cliente</p>
                <p className="font-medium">{saleDetail.owner_name || 'Cliente ocasional'}</p>
              </div>
              <div>
                <p className="text-sm text-taupe-500">Pago</p>
                <div className="flex items-center gap-2">
                  {PAYMENT_METHOD_ICONS[saleDetail.payment_method]}
                  <span className="font-medium">
                    {PAYMENT_METHOD_LABELS[saleDetail.payment_method]}
                    {saleDetail.payment_method === 'card' && saleDetail.card_type && (
                      ` · ${saleDetail.card_type === 'credit'
                        ? `Crédito${saleDetail.card_installments ? ` ${saleDetail.card_installments} cuota${saleDetail.card_installments !== 1 ? 's' : ''}` : ''}`
                        : 'Débito'}`
                    )}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <p className="text-sm text-taupe-500 mb-2">Items</p>
              <div className="border rounded-lg divide-y">
                {saleDetail.items.map((item) => (
                  <div key={item.id} className="p-3 flex justify-between">
                    <div>
                      <p className="font-medium">{item.product_name}</p>
                      <p className="text-sm text-taupe-500">
                        {item.quantity} x {formatCurrency(item.unit_price)}
                      </p>
                    </div>
                    <p className="font-medium">{formatCurrency(item.subtotal)}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t pt-4 flex justify-between text-lg font-bold">
              <span>Total:</span>
              <span>{formatCurrency(saleDetail.total)}</span>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => {
                  if (tenant) generateSaleReceipt(saleDetail, tenant);
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                Descargar PDF
              </Button>
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => {
                  setIsDetailModalOpen(false);
                  setComprobanteSale(saleDetail);
                }}
              >
                <FileText className="h-4 w-4 mr-2" />
                Comprobante
              </Button>
              <Button variant="secondary" className="flex-1" onClick={() => setIsDetailModalOpen(false)}>
                Cerrar
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de cancelación */}
      <Modal
        isOpen={!!cancelConfirm}
        onClose={() => setCancelConfirm(null)}
        title="Cancelar Venta"
      >
        <div className="space-y-4">
          <p>¿Cancelar la venta <strong>{cancelConfirm?.receipt_number}</strong>?</p>
          <p className="text-sm text-taupe-500">Se restaurará el stock de los productos.</p>
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setCancelConfirm(null)}>
              No
            </Button>
            <Button variant="danger" className="flex-1" onClick={handleCancel} disabled={cancelSale.isPending}>
              {cancelSale.isPending ? 'Cancelando...' : 'Sí, cancelar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de confirmación de cobro */}
      <Modal
        isOpen={isChargeModalOpen}
        onClose={() => setIsChargeModalOpen(false)}
        title="Confirmar Cobro"
      >
        {selectedOrder && (
          <div className="space-y-4">
            <div className="bg-cream-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Dog className="h-5 w-5 text-orange-500" />
                <span className="font-medium">{selectedOrder.patient_name}</span>
                <span className="text-taupe-500">- {selectedOrder.owner_name}</span>
              </div>

              <div className="space-y-1 text-sm mb-3">
                {selectedOrder.items.map((item) => {
                  const product = allProducts.find(p => p.id === item.product_id);
                  let price: number;
                  if (orderConRecargo) {
                    // Si es producto por ML, aplicar recargo configurable
                    if (item.ml_quantity && item.ml_quantity > 0) {
                      price = Math.round(item.unit_price * cardSurchargeMultiplier);
                    } else {
                      price = product?.list_price ?? item.unit_price;
                    }
                  } else {
                    price = item.unit_price;
                  }
                  return (
                    <div key={item.id} className="flex justify-between">
                      <span>{item.product_name} x{item.quantity}</span>
                      <span>{formatCurrency(price * item.quantity)}</span>
                    </div>
                  );
                })}
              </div>

              {orderExtraItems.length > 0 && (
                <div className="pt-3 border-t border-cream-200">
                  <p className="text-green-600 font-medium text-sm mb-2">+ Productos adicionales</p>
                  <div className="space-y-1 text-sm">
                    {orderExtraItems.map((item, index) => {
                      // Item customizado
                      if (!item.product) {
                        const basePrice = item.customPrice ?? 0;
                        const price = (orderConRecargo)
                          ? Math.round(basePrice * cardSurchargeMultiplier)
                          : basePrice;
                        return (
                          <div key={`custom-${index}`} className="flex justify-between">
                            <span className="flex items-center gap-1">
                              <span className="text-xs bg-purple-200 text-purple-700 px-1 rounded">Otro</span>
                              {item.customName} x{item.quantity}
                            </span>
                            <span>{formatCurrency(price * item.quantity)}</span>
                          </div>
                        );
                      }
                      // Producto normal
                      const product = item.product;
                      const price = item.customPrice ?? (
                        (orderConRecargo)
                          ? (product.list_price ?? product.sale_price ?? 0)
                          : (product.sale_price ?? 0)
                      );
                      return (
                        <div key={product.id} className="flex justify-between">
                          <span>{product.name} x{item.quantity}</span>
                          <span>{formatCurrency(price * item.quantity)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
              <span className="font-medium">Pago:</span>
              <span className="flex items-center gap-2">
                {PAYMENT_METHOD_ICONS[orderPaymentMethod]}
                {PAYMENT_METHOD_LABELS[orderPaymentMethod]}
              </span>
            </div>

            {/* Descuento */}
            <div className="border rounded-lg p-3 space-y-2">
              <p className="text-sm font-medium text-gray-600">Descuento (opcional)</p>
              <div className="flex gap-2">
                <button
                  className={`flex-1 py-1.5 text-sm rounded-lg border font-medium transition-colors ${discountType === 'percentage' ? 'bg-orange-100 border-orange-400 text-orange-700' : 'bg-white border-gray-200 text-gray-500'}`}
                  onClick={() => { setDiscountType('percentage'); setDiscountValue(''); }}
                >% Porcentaje</button>
                <button
                  className={`flex-1 py-1.5 text-sm rounded-lg border font-medium transition-colors ${discountType === 'fixed' ? 'bg-orange-100 border-orange-400 text-orange-700' : 'bg-white border-gray-200 text-gray-500'}`}
                  onClick={() => { setDiscountType('fixed'); setDiscountValue(''); }}
                >$ Monto fijo</button>
              </div>
              <input
                type="number"
                min="0"
                placeholder={discountType === 'percentage' ? 'Ej: 10 (10%)' : 'Ej: 5000'}
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
              />
            </div>

            <div className="space-y-1">
              {discountNum > 0 && (
                <div className="flex justify-between items-center text-sm text-gray-400">
                  <span>Subtotal:</span>
                  <span className="line-through">{formatCurrency(orderTotalWithExtras)}</span>
                </div>
              )}
              {discountNum > 0 && (
                <div className="flex justify-between items-center text-sm text-green-600 font-medium">
                  <span>Descuento:</span>
                  <span>-{discountType === 'percentage' ? `${discountNum}%` : formatCurrency(discountNum)}</span>
                </div>
              )}
              <div className="flex justify-between items-center text-xl font-bold">
                <span>Total:</span>
                <span className="text-orange-600">{formatCurrency(discountedTotal)}</span>
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="secondary" className="flex-1" onClick={() => setIsChargeModalOpen(false)}>
                Cancelar
              </Button>
              <Button
                className="flex-1 bg-orange-500 hover:bg-orange-600"
                onClick={handleChargeOrder}
                disabled={chargeOrder.isPending}
              >
                {chargeOrder.isPending ? 'Procesando...' : 'Confirmar'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Confirmación antes de cobrar */}
      <Modal
        isOpen={showConfirmSale}
        onClose={() => setShowConfirmSale(false)}
        title="Confirmar cobro"
        size="lg"
      >
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="text-gray-500">
              Cliente: <span className="font-medium text-gray-900">{selectedOwnerLabel}</span>
            </span>
            <span className="text-gray-500">
              Pago: <span className="font-medium text-gray-900">
                {PAYMENT_METHOD_LABELS[newSalePayment]}
                {newSalePayment === 'card' && ` · ${cardType === 'credit' ? `Crédito ${cardInstallments} cuota${cardInstallments !== 1 ? 's' : ''}` : 'Débito'}`}
              </span>
            </span>
          </div>

          <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-lg">
            {newSaleItems.map((item) => {
              const base = unitBaseFor(item, currentTier) * item.quantity;
              const disc = lineDiscountFor(item, currentTier);
              const name = item.product ? item.product.name : (item.customName || 'Item');
              return (
                <div key={item.id} className="flex items-start justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm text-gray-900 break-words">
                      {name} <span className="text-gray-400">x{item.quantity}</span>
                    </p>
                    {disc > 0 && (
                      <p className="text-xs text-green-600">
                        Desc. {item.discountType === 'fixed' ? formatCurrency(item.discountValue || 0) : `${item.discountValue}%`}
                        {' · '}−{formatCurrency(disc)}
                      </p>
                    )}
                  </div>
                  <div className="text-right whitespace-nowrap">
                    {disc > 0 && <p className="text-xs text-gray-400 line-through">{formatCurrency(base)}</p>}
                    <p className="text-sm font-semibold text-gray-900">{formatCurrency(lineTotalFor(item, currentTier))}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Subtotal</span>
              <span className="font-medium text-gray-900">{formatCurrency(newSaleTotal)}</span>
            </div>
            {directDiscountNum > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Descuento general {directDiscountType === 'percentage' ? `(${directDiscountNum}%)` : ''}</span>
                <span>−{formatCurrency(newSaleTotal - newSaleTotalWithDiscount)}</span>
              </div>
            )}
            {roundingActive && (
              <div className="flex justify-between text-amber-700">
                <span>Redondeo</span>
                <span>{roundingAdjustment >= 0 ? '+' : '−'}{formatCurrency(Math.abs(roundingAdjustment))}</span>
              </div>
            )}
            <div className="flex justify-between items-center border-t pt-2 mt-1">
              <span className="font-semibold text-gray-900">Total a cobrar</span>
              <span className="text-2xl font-bold text-green-600">{formatCurrency(finalChargeTotal)}</span>
            </div>
          </div>

          {newSaleNotes && <p className="text-xs text-gray-500 italic">Nota: {newSaleNotes}</p>}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={() => setShowConfirmSale(false)}
              disabled={createSale.isPending}
              className="flex-1 py-2.5 rounded-lg border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
            >
              Volver
            </button>
            <button
              type="button"
              onClick={handleCreateSale}
              disabled={createSale.isPending || newSaleItems.length === 0 || hasItemsWithoutPrice || !splitValido}
              className="flex-1 py-2.5 rounded-lg bg-green-600 text-white font-bold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              <Check className="h-5 w-5" />
              {createSale.isPending ? 'Procesando...' : 'Confirmar cobro'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Comprobante de la venta: ticket comun o factura fiscal */}
      {comprobanteSale && tenant ? (
        <ComprobanteModal
          sale={comprobanteSale}
          tenant={tenant}
          onClose={() => setComprobanteSale(null)}
        />
      ) : null}
    </div>
  );
}
